/**
 * Insidermark site content. Three live languages (TR default, EN, FR) share one
 * structure so every route renders from the same shape. Slugs stay English
 * in every language on purpose (maintenance rule from the site spec).
 */
export type Lang = "en" | "fr" | "tr";
export const LANGS: Lang[] = ["tr", "en", "fr"];
export const DEFAULT_LANG: Lang = "tr";
export const isLang = (value: string): value is Lang => (LANGS as string[]).includes(value);

/** Replace with the live WhatsApp Business number (international format, digits only). */
export const WHATSAPP_NUMBER = "905000000000";
export const CONTACT_EMAIL = "rfq@insidermark.com";
/** Brand ground colour, used for theme-color meta (kept out of route files). */
export const BRAND_GROUND = "#0b1622";

export type DivisionSlug =
  | "automotive-heavy-duty"
  | "engineering-railway"
  | "agriculture-livestock"
  | "industrial-plants"
  | "custom-sourcing";

export type RfqFlow = "spare-parts" | "custom-manufacturing" | "turnkey-project";

export interface Division {
  slug: DivisionSlug;
  code: string;
  icon: string;
  image: string;
  name: string;
  short: string;
  scope: string;
  products: { name: string; detail: string }[];
  capacity: { label: string; value: string }[];
  buyer: string;
  markets: string;
  constraint: string;
  rfq: RfqFlow;
}

export interface Market {
  slug: string;
  name: string;
  region: string;
  marker: string;
  divisions: DivisionSlug[];
  why: string;
  importRules: string[];
  logistics: { origin: string; destination: string; transit: string };
  reference: string;
  language: string;
  phase: 1 | 2;
}

export interface Step {
  title: string;
  body: string;
}

export interface Dictionary {
  langName: string;
  nav: {
    divisions: string;
    advantage: string;
    how: string;
    markets: string;
    network: string;
    about: string;
    contact: string;
    rfq: string;
    whatsapp: string;
    menu: string;
    close: string;
  };
  journey: {
    id: string;
    label: string;
    kicker?: string;
    title: string;
    body: string;
    tags?: string[];
  }[];
  hero: { secondary: string; waText: string; slogan: string };
  partners: {
    title: string;
    intro: string;
    note: string;
    items: { name: string; logoKey: string; sector: string; body: string; meta: string; division: DivisionSlug }[];
  };
  engines: {
    title: string;
    intro: string;
    items: { tag: string; name: string; body: string }[];
  };
  advantage: {
    title: string;
    subtitle: string;
    intro: string;
    blocks: { n: string; label: string; myth: string; mythBody: string; truth: string; truthBody: string }[];
    ctaLine: string;
    ctaRfq: string;
    ctaDesk: string;
  };
  proof: { value: string; label: string }[];
  divisionsSection: { title: string; intro: string; open: string };
  layers: {
    title: string;
    intro: string;
    items: { name: string; body: string }[];
  };
  consolidation: {
    eyebrow: string;
    title: string;
    body: string;
    nodes: { konya: string; ankara: string; istanbul: string; cluster: string };
    ports: { mersin: string; izmir: string; ambarli: string };
    portsNote: { mersin: string; izmir: string; ambarli: string };
    africa: string;
    manifestTitle: string;
    foot: { label: string; value: string }[];
    manifest: { label: string; value: string }[];
  };
  marketsSection: {
    title: string;
    body: string;
    priorities: { name: string; countries: string; slug: string }[];
    open: string;
  };
  process: { title: string; intro: string; steps: Step[]; note: string };
  references: {
    eyebrow: string;
    title: string;
    items: { title: string; where: string; body: string; image: string; facts: string[] }[];
    disclaimer: string;
  };
  closing: { title: string; body: string };
  footer: {
    address: string;
    columns: { title: string; links: { label: string; to: string }[] }[];
    legal: string;
    languages: string;
    privacy: string;
    terms: string;
  };
  divisions: Division[];
  divisionPage: {
    products: string;
    capacity: string;
    buyer: string;
    markets: string;
    constraint: string;
    rfq: string;
    back: string;
    catalog: string;
    catalogNote: string;
  };
  markets: Market[];
  marketPage: {
    activeDivisions: string;
    importRules: string;
    importNote: string;
    logistics: string;
    origin: string;
    destination: string;
    transit: string;
    reference: string;
    language: string;
    rfq: string;
    all: string;
    phase2: string;
  };
  how: {
    title: string;
    intro: string;
    representation: { title: string; body: string[] };
    quality: { title: string; body: string[]; report: { label: string; value: string }[] };
    consolidation: { title: string; body: string[] };
    incoterms: { title: string; intro: string; terms: { code: string; name: string; body: string }[] };
    payment: { title: string; body: string[] };
    faq: string;
    bypass: { q: string; a: string };
  };
  network: {
    title: string;
    intro: string;
    partners: { area: string; since: string; capacity: string; certs: string; division: string }[];
    corridor: string;
    rule: string;
  };
  about: {
    title: string;
    vision: { title: string; body: string };
    mission: { title: string; body: string };
    values: { title: string; items: { name: string; body: string }[] };
    notWeAre: { title: string; items: string[] };
    whyTurkiye: { title: string; body: string[] };
    team: { title: string; body: string };
  };
  contact: {
    title: string;
    body: string;
    email: string;
    whatsapp: string;
    offices: string;
    hours: string;
    languagesSpoken: string;
    spokenList: string;
    hoursValue: string;
    desk: { title: string; body: string; cta: string };
  };
  rfq: {
    title: string;
    intro: string;
    flows: { flow: RfqFlow; title: string; body: string; time: string }[];
    common: {
      company: string;
      contact: string;
      email: string;
      phone: string;
      country: string;
      language: string;
      port: string;
      incoterms: string;
      consolidation: string;
      consolidationHint: string;
      notes: string;
      file: string;
      fileHint: string;
      submit: string;
      submitting: string;
      privacy: string;
      required: string;
      error: string;
      consent: string;
      consentLink: string;
      whatsappContinue: string;
      consentError: string;
    };
    spare: {
      brand: string;
      brandHint: string;
      template: string;
      templateHint: string;
      upload: string;
    };
    custom: {
      sector: string;
      material: string;
      quantity: string;
      series: string;
      seriesOptions: string[];
      upload: string;
      nda: string;
    };
    turnkey: {
      plant: string;
      plantOptions: string[];
      capacity: string;
      capacityHint: string;
      site: string;
      siteOptions: string[];
      budget: string;
      budgetOptions: string[];
      timeline: string;
      timelineOptions: string[];
      financing: string;
      financingOptions: string[];
    };
    received: {
      title: string;
      body: string;
      steps: string[];
      ref: string;
      home: string;
    };
  };
  languagesNote: string;
  a11y: { keyFigures: string; routeDiagram: string; marketsMap: string; prevSteps: string; nextSteps: string; language: string; primary: string; chapters: string };
  faq: { title: string; intro: string; items: { q: string; a: string }[] };
  legal: {
    updated: string;
    privacy: { title: string; intro: string; sections: { h: string; p: string[] }[] };
    terms: { title: string; intro: string; sections: { h: string; p: string[] }[] };
  };
}

const divisionsEn: Division[] = [
  {
    slug: "automotive-heavy-duty",
    code: "A",
    icon: "/assets/icons/automotive.webp",
    image: "/assets/divisions/automotive.webp",
    name: "Automotive & Commercial Vehicles",
    short: "Complete commercial vehicles, heavy-duty spare parts and a consolidation warehouse.",
    scope:
      "Turnkey export of buses, tractor units and special-purpose commercial vehicles such as ambulances, plus heavy-duty spare parts and consolidation of mixed parts orders in one warehouse. Strongest on MAN, Mercedes-Benz and TEMSA platforms.",
    products: [
      { name: "Complete vehicle export", detail: "Buses, tractor units, trucks and special-purpose vehicles, delivered turnkey for Africa, the Middle East and Central Asia" },
      { name: "Heavy-duty spare parts", detail: "Brake systems to suspension, engine, gearbox, differential, electrical, body, chassis, trailer and air spring groups" },
      { name: "Parts consolidation", detail: "Mixed parts requests for several European and local brands collected in one warehouse and shipped on one freight" },
      { name: "Fleet conversion", detail: "Bus and minibus refurbishment, redesign and fleet handover" },
    ],
    capacity: [
      { label: "Distribution", value: "7,000+ delivery points in 81 provinces" },
      { label: "Warehousing", value: "50,000 m2+ across 7 sites" },
      { label: "Brand coverage", value: "60+ world brands in the partner's range" },
      { label: "Export reach", value: "100+ countries" },
    ],
    buyer: "Fleet operators, importers and public transport authorities holding MAN or Mercedes-Benz heavy vehicle fleets. Typical order: 50 to 500 OEM line items per consignment.",
    markets: "Africa, the Middle East and Central Asia. Complete vehicles are not offered to the EU or the US because of type approval.",
    constraint: "EU and US aftermarket expects brand and certification evidence per part; we quote there only with documented origin.",
    rfq: "spare-parts",
  },
  {
    slug: "engineering-railway",
    code: "B",
    icon: "/assets/icons/engineering.webp",
    image: "/assets/divisions/engineering.webp",
    name: "Advanced Engineering & Railway",
    short: "Railway systems, defence-industry components, hydraulic cylinders, casting and CNC.",
    scope:
      "Railway systems and components, defence-industry parts, hydraulic cylinders for heavy equipment, and made-to-drawing casting, forging and CNC machining with Insidermark quality verification.",
    products: [
      { name: "Railway systems", detail: "Wagon brake systems and railway infrastructure components under TSI and EN 15085 welding" },
      { name: "Defence-industry components", detail: "High-security components for the defence industry" },
      { name: "Hydraulic cylinders", detail: "Industrial and heavy equipment cylinders, standard and custom" },
      { name: "Casting, forging and CNC", detail: "Built from your blueprint and material specification, forged and cast components up to 200 kg, workpieces up to 30 tonnes" },
      { name: "Private label manufacturing", detail: "Your drawing, your brand, our partner's line" },
    ],
    capacity: [
      { label: "Covered production", value: "50,000 m2" },
      { label: "Forging and casting", value: "up to 200 kg" },
      { label: "Machining", value: "up to 30 t" },
      { label: "Certification", value: "TSI EN 15566, EN 15085, NDT Level 2" },
    ],
    buyer: "OEMs and tier suppliers in Europe, Spain, the US and Mexico who audit their suppliers and require quality management documents. Typical order: series production from a technical drawing.",
    markets: "Europe, Spain, the US and Mexico for OEM and private label; North Africa and the Middle East for railway components.",
    constraint: "Railway components are quoted only with TSI and EN 15085 coverage. No certificate, no offer.",
    rfq: "custom-manufacturing",
  },
  {
    slug: "agriculture-livestock",
    code: "C",
    icon: "/assets/icons/agriculture.webp",
    image: "/assets/divisions/agriculture.webp",
    name: "Agriculture & Livestock Machinery",
    short: "Heavy tillage, harvesting, feed mixing and automatic milking for modern farms.",
    scope:
      "Machinery for modern agriculture and industrial cattle farming: heavy soil tillage and harvest equipment, feed mixing machines and automatic milking systems, delivered from the factory line to your project site.",
    products: [
      { name: "Heavy tillage and harvest", detail: "Tractor attachments, pneumatic seeders, disc harrows, rotary tillers and heavy harvesting machines" },
      { name: "Feed mixing machinery", detail: "Industrial feed mixers for cattle farms" },
      { name: "Automatic milking systems", detail: "Fully automatic milking stations for dairy facilities" },
      { name: "Livestock facility technology", detail: "The machinery layer of large cattle investments, scoped per project" },
    ],
    capacity: [
      { label: "Tillage partner since", value: "1980, Konya" },
      { label: "Reference", value: "Azerbaijan national tender" },
      { label: "Reference", value: "First Turkish agricultural machinery export to the US" },
      { label: "Delivery", value: "From the factory line to your project site" },
    ],
    buyer: "Farms, cooperatives, dairy and cattle investors, ministries and development-bank funded programmes. Typical order: a machinery fleet or the equipment of a livestock facility.",
    markets: "Worldwide, including the US.",
    constraint: "CE and machinery directive conformity required for the EU.",
    rfq: "turnkey-project",
  },
  {
    slug: "industrial-plants",
    code: "D",
    icon: "/assets/icons/milling.webp",
    image: "/assets/divisions/milling.webp",
    name: "Industrial Plant Solutions",
    short: "Milling machinery and turnkey factory installation, from machines to whole plants.",
    scope:
      "High-capacity flour, semolina and feed milling machinery, silo and grain handling systems, and complete turnkey plant installation: new factories, capacity upgrades and mechanical integration managed end to end.",
    products: [
      { name: "Turnkey flour mills", detail: "From 50 to 500 t/day, including installation, commissioning and operator training" },
      { name: "Semolina and feed plants", detail: "Durum lines and compound feed plants" },
      { name: "Silo and grain handling", detail: "Steel silos, conveying, intake and loading systems" },
      { name: "Plant installation and upgrade", detail: "Greenfield factory set-up, capacity increase, roller stand replacement, automation and mechanical integration" },
    ],
    capacity: [
      { label: "Reference", value: "150 t/day, 10 roller mills, Algeria" },
      { label: "Delivery", value: "35 days production, 15 days installation" },
      { label: "Since", value: "1994, Konya" },
      { label: "Service", value: "Installation, training, spare parts plan" },
    ],
    buyer: "Investors, millers and agri-food groups planning a new plant or a capacity upgrade. Typical order: a turnkey plant with a spare parts plan and financing structure.",
    markets: "Worldwide on request, strongest in Africa.",
    constraint: "Installation, commissioning and training are always included in the price. A plant quoted without a spare parts plan is not a plant.",
    rfq: "turnkey-project",
  },
  {
    slug: "custom-sourcing",
    code: "E",
    icon: "/assets/icons/commodities.webp",
    image: "/assets/divisions/custom-sourcing.webp",
    name: "Special & Custom Sourcing",
    short: "Our project desk for machines and parts outside the standard portfolio.",
    scope:
      "For specific machines and parts that are not in our portfolio or that need special production, we scan Türkiye's heavy-industry ecosystem, match the right manufacturer, optimise cost and manage the production on site to our own quality standards.",
    products: [
      { name: "Manufacturer matching", detail: "The right Turkish producer for your specification, chosen from a qualified supplier base" },
      { name: "Cost optimisation", detail: "Comparable quotations structured into one clear offer" },
      { name: "Supervised production", detail: "On-site follow-up from order to loading" },
      { name: "Insidermark verification", detail: "Product verification before dispatch, with the same QC report as every other consignment" },
    ],
    capacity: [
      { label: "Reach", value: "Türkiye's heavy-industry ecosystem" },
      { label: "Process", value: "Match, supervise, verify" },
      { label: "Desk", value: "Direct line to the managing partner" },
    ],
    buyer: "Buyers with a specific machine or part that no catalogue covers. Typical order: a drawing or specification with a target quantity.",
    markets: "Worldwide on request, strongest in Africa.",
    constraint: "We accept a request only when a qualified manufacturer can be identified and verified. If we cannot, we say so at the first answer.",
    rfq: "custom-manufacturing",
  },
];

const marketsEn: Market[] = [
  {
    slug: "algeria", name: "Algeria", region: "North Africa", marker: "algeria", phase: 1,
    divisions: ["industrial-plants", "agriculture-livestock", "automotive-heavy-duty", "custom-sourcing"],
    why: "Our partner network's strongest turnkey milling reference stands in Algeria, and the short sea leg from İzmir keeps transit predictable.",
    importRules: [
      "Pre-shipment conformity assessment programmes apply to many product groups; scope changes often, so the current regime is confirmed before every order.",
      "Commercial documents in French; Arabic accepted for customs correspondence.",
      "Payment for new buyers: confirmed letter of credit or advance payment.",
    ],
    logistics: { origin: "İzmir or Mersin", destination: "Algiers, Oran", transit: "5 to 8 days" },
    reference: "150 t/day flour mill with 10 roller mills, commissioned in 50 days (35 days production, 15 days installation).",
    language: "French and Arabic speaking counterpart.",
  },
  {
    slug: "tunisia", name: "Tunisia", region: "North Africa", marker: "tunisia", phase: 1,
    divisions: ["industrial-plants", "agriculture-livestock", "custom-sourcing", "automotive-heavy-duty"],
    why: "Field connections on the ground and short sea legs from İzmir make Tunisia a natural entry market for mills, farm machinery and heavy vehicle parts.",
    importRules: [
      "Technical control at import applies to defined product lists; verified per HS code before shipment.",
      "French language documentation.",
      "Short offer validity (7 to 15 days) to protect against currency movement.",
    ],
    logistics: { origin: "İzmir", destination: "Radès (Tunis), Sfax", transit: "4 to 7 days" },
    reference: "Nearest reference: the Algerian turnkey mill, one border away.",
    language: "French and Arabic speaking counterpart.",
  },
  {
    slug: "morocco", name: "Morocco", region: "North Africa", marker: "morocco", phase: 1,
    divisions: ["agriculture-livestock", "industrial-plants", "automotive-heavy-duty"],
    why: "Farm mechanisation demand is structural, and Morocco's SIAM fair is on our annual field calendar.",
    importRules: [
      "Conformity control for regulated industrial products; scope confirmed per order.",
      "French documentation; Arabic labelling rules for consumer goods.",
      "Letter of credit is the norm for first transactions.",
    ],
    logistics: { origin: "İzmir", destination: "Casablanca, Tanger Med", transit: "8 to 12 days" },
    reference: "Tillage machinery references from the Konya cluster.",
    language: "French and Arabic speaking counterpart.",
  },
  {
    slug: "senegal", name: "Senegal", region: "West Africa", marker: "senegal", phase: 1,
    divisions: ["industrial-plants", "agriculture-livestock", "automotive-heavy-duty"],
    why: "Development-bank funded agriculture and food infrastructure programmes make Senegal our West African entry market.",
    importRules: [
      "Import verification programmes may require pre-shipment inspection; confirmed per order.",
      "French documentation throughout.",
      "Financing usually decides the project: Eximbank buyer credit and export credit insurance are structured into the offer.",
    ],
    logistics: { origin: "Ambarlı or Kocaeli", destination: "Dakar", transit: "18 to 25 days" },
    reference: "Turnkey plant model proven in North Africa; installation and training included.",
    language: "French speaking counterpart.",
  },
  {
    slug: "nigeria", name: "Nigeria", region: "West Africa", marker: "nigeria", phase: 1,
    divisions: ["automotive-heavy-duty", "industrial-plants", "agriculture-livestock"],
    why: "The largest heavy vehicle park in West Africa and a milling sector that keeps expanding capacity.",
    importRules: [
      "SONCAP conformity assessment is mandatory for regulated products; an uncertified container waits at the port and generates storage costs.",
      "English documentation.",
      "New buyers: confirmed letter of credit or advance payment; currency transfer delays are a real risk, so offer validity stays short.",
    ],
    logistics: { origin: "Ambarlı or Kocaeli", destination: "Lagos (Apapa, Tin Can)", transit: "20 to 28 days" },
    reference: "MAN and Mercedes-Benz spare parts programmes from our automotive partners.",
    language: "English speaking counterpart.",
  },
  { slug: "libya", name: "Libya", region: "North Africa", marker: "libya", phase: 2, divisions: ["automotive-heavy-duty", "industrial-plants"], why: "Heavy vehicle park and grain processing needs close to Mersin.", importRules: ["Regime confirmed per order."], logistics: { origin: "Mersin", destination: "Tripoli, Misrata", transit: "4 to 7 days" }, reference: "Programme in preparation.", language: "Arabic speaking counterpart." },
  { slug: "egypt", name: "Egypt", region: "North Africa", marker: "egypt", phase: 2, divisions: ["industrial-plants", "automotive-heavy-duty", "engineering-railway"], why: "Milling capacity and railway component demand.", importRules: ["Pre-registration and conformity requirements apply; confirmed per order."], logistics: { origin: "Mersin", destination: "Alexandria, Damietta", transit: "3 to 6 days" }, reference: "Programme in preparation.", language: "Arabic and English speaking counterpart." },
  { slug: "ivory-coast", name: "Ivory Coast", region: "West Africa", marker: "ivory-coast", phase: 2, divisions: ["industrial-plants", "agriculture-livestock"], why: "Agri-food investment corridor.", importRules: ["Import verification programme; confirmed per order."], logistics: { origin: "Ambarlı", destination: "Abidjan", transit: "20 to 26 days" }, reference: "Programme in preparation.", language: "French speaking counterpart." },
  { slug: "ghana", name: "Ghana", region: "West Africa", marker: "ghana", phase: 2, divisions: ["agriculture-livestock", "automotive-heavy-duty"], why: "Mechanisation programmes and fleet renewal.", importRules: ["Conformity assessment programme; confirmed per order."], logistics: { origin: "Ambarlı", destination: "Tema", transit: "20 to 26 days" }, reference: "Programme in preparation.", language: "English speaking counterpart." },
  { slug: "kenya", name: "Kenya", region: "East Africa", marker: "kenya", phase: 2, divisions: ["industrial-plants", "agriculture-livestock", "automotive-heavy-duty"], why: "East African hub for milling and farm mechanisation.", importRules: ["PVoC pre-export verification is mandatory for regulated goods."], logistics: { origin: "Mersin", destination: "Mombasa", transit: "18 to 24 days" }, reference: "Programme in preparation.", language: "English speaking counterpart." },
  { slug: "ethiopia", name: "Ethiopia", region: "East Africa", marker: "ethiopia", phase: 2, divisions: ["industrial-plants", "agriculture-livestock"], why: "Large scale agriculture and food processing programmes.", importRules: ["Conformity and foreign currency allocation rules; confirmed per order."], logistics: { origin: "Mersin", destination: "Djibouti, onward by rail", transit: "18 to 24 days plus inland" }, reference: "Programme in preparation.", language: "English speaking counterpart." },
  { slug: "tanzania", name: "Tanzania", region: "East Africa", marker: "tanzania", phase: 2, divisions: ["industrial-plants", "agriculture-livestock"], why: "Grain processing and farm mechanisation demand.", importRules: ["PVoC pre-shipment verification applies."], logistics: { origin: "Mersin", destination: "Dar es Salaam", transit: "20 to 26 days" }, reference: "Programme in preparation.", language: "English speaking counterpart." },
];

const en: Dictionary = {
  langName: "English",
  nav: {
    divisions: "Sectors", how: "How we work", markets: "Markets", network: "Network", about: "About", contact: "Contact",
    rfq: "Request a quotation", whatsapp: "WhatsApp", menu: "Menu", close: "Close", advantage: "Beyond Sourcing",
  },
  journey: [
    { id: "origin", label: "Origin", title: "Your integrated sourcing and operations hub in Türkiye.", body: "Through authorised manufacturer partnerships and our on-demand custom sourcing network, we meet your heavy industry needs from one centre. We follow your orders on site during production, verify products before shipment and organise your multi-brand consolidation end to end.", tags: ["Konya", "Ankara", "İstanbul"] },
    { id: "divisions", label: "Sectors", title: "Five sectors, one counterpart.", body: "Commercial vehicles, engineering and railway, agriculture and livestock machinery, industrial plants, and a custom sourcing desk. You deal with one accountable person.", tags: ["Özbaşak"] },
    { id: "inspection", label: "Inspection", title: "Verified on site before it ships.", body: "We follow the order on the production line and verify the goods before the container closes. Every consignment leaves with a quality control report carrying the Insidermark seal.", tags: ["Mapar", "Hıdırusta"] },
    { id: "consolidation", label: "Consolidation", title: "One container. One invoice. One shipment.", body: "Items from several manufacturers leave in a single mixed container with one set of export documents.", tags: ["FMC"] },
    { id: "world", label: "World", title: "Your global sourcing partner, from Türkiye.", body: "Active across Africa, the Middle East, Central Asia, Europe and the Americas. Support in Turkish, English and French, a quotation within 48 hours." },
  ],
  hero: { secondary: "How we work", waText: "Hello Insidermark, I would like a quotation.", slogan: "Your global sourcing partner, from Türkiye." },
  partners: {
    title: "Our partners",
    intro: "The manufacturers behind every consignment. Each partner stands for one sector of our five.",
    note: "Names and marks belong to their respective owners.",
    items: [
      { name: "Mapar", logoKey: "mapar", sector: "Automotive & Commercial Vehicles", body: "Authorised dealer and service for MAN, TEMSA, FUSO Canter and ZF. Complete bus, truck and special-purpose vehicle export, bus and minibus conversion.", meta: "Since 1980 · Ankara, Bursa, İzmir", division: "automotive-heavy-duty" },
      { name: "Hıdırusta", logoKey: "hidirusta", sector: "Heavy-duty spare parts", body: "Wholesale distribution for MAN and Mercedes-Benz parts across 7,000+ delivery points in 81 provinces, 50,000 m2+ of warehousing and our mixed-parts consolidation point.", meta: "Since 1978 · İstanbul", division: "automotive-heavy-duty" },
      { name: "FMC", logoKey: "fmc", sector: "Advanced Engineering & Railway", body: "Forging, casting and machining with TSI EN 15566, EN 15085 and NDT Level 2. Forged parts from 0.5 to 200 kg, machining to 30 t, exports to 45 countries.", meta: "About 50 years · Konya", division: "engineering-railway" },
      { name: "Özbaşak", logoKey: "ozbasak", sector: "Industrial Plant Solutions", body: "Turnkey flour and semolina plants, silos, automation and after-sales. A 150 t/day mill in Algeria was commissioned in 50 days.", meta: "Since 1994 · Konya", division: "industrial-plants" },
    ],
  },
  engines: {
    title: "Two engines, one counterpart.",
    intro: "Our supply strength stands on two wings. Use either, or both in the same container.",
    items: [
      { tag: "Wing 1", name: "Authorised partnerships", body: "We act as the outside operations desk of established Turkish manufacturers in our network. The direct strength of sector leaders, reached through one address." },
      { tag: "Wing 2", name: "On-demand and custom sourcing", body: "For projects that do not fit a catalogue, our independent project desk connects you to the right manufacturer in Türkiye's heavy industry. Do not be limited by a catalogue: tell us the need." },
    ],
  },
  advantage: {
    title: "Beyond Sourcing",
    subtitle: "Where traditional sourcing ends: why buyers work with Insidermark instead of going to the factory alone.",
    intro: "In international heavy industry trade the largest cost is not the product. It is unmanaged operations, blind production and fragmented logistics. When Insidermark sits at the table we do not only find the product you ask for; we remove the hidden costs and operational risks of the supply chain.",
    blocks: [
      { n: "01", label: "Price position", myth: "The direct-to-factory illusion", mythBody: "Foreign buyers often assume that reaching a large manufacturer directly gives the lowest price. In practice, large factories usually quote single, irregular buyers their standard export list price.", truth: "Partner-level access", truthBody: "We work with our manufacturing partners as a recurring, higher-volume counterpart. Through our agreements and consolidated purchasing we aim to bring you partner-level terms and production priority that a one-off buyer typically does not receive." },
      { n: "02", label: "Process", myth: "Corporate bureaucracy and fragmented logistics", mythBody: "Large manufacturers move slowly. Special specifications get stuck, small mixed orders are declined, and buying from different brands means separate freight and separate customs for each.", truth: "Consolidation power", truthBody: "We are the operations desk inside the process, not a customer waiting at the gate. We speed up special production requests through our agreements, and combine mixed orders from different brands at one logistics point, in one container, so you stop paying five freights and five customs entries." },
      { n: "03", label: "Risk", myth: "The quality blind spot", mythBody: "After the payment leaves, the flight is blind. Will the product be made to tolerance, will it be loaded complete? Surprises appear only when the goods reach your customs, and there is no way back.", truth: "On-site supervision and contractual cover", truthBody: "We do not manage from a desk or by email. While your order is on the line we go to the site, follow production and apply Insidermark QC verification before the container is sealed. Your trade rests on written agreements and on our supervision in the field." },
    ],
    ctaLine: "Connect to Türkiye's heavy industry as a priority partner, not a standard customer.",
    ctaRfq: "Initialise a B2B RFQ",
    ctaDesk: "Executive Desk",
  },
  proof: [
    { value: "5", label: "sectors under one counterpart" },
    { value: "5", label: "manufacturing partners, Konya to İstanbul" },
    { value: "1975", label: "oldest partner founded" },
    { value: "100+", label: "countries our partners already export to" },
  ],
  divisionsSection: { title: "Five core sectors, one counterpart.", intro: "Instead of writing to five companies in Türkiye, you work with one. Open a sector to see scope, capacity and the markets it fits.", open: "Open sector" },
  layers: {
    title: "Three pillars, delivered together.",
    intro: "Access alone would make us a broker. The second and third pillars are the part that is hard to replace.",
    items: [
      { name: "Partner-level pricing", body: "Our price reflects the terms we hold with the manufacturer plus the operation that stands behind it. Every quotation states what the price covers, so there are no surprise costs." },
      { name: "On-site verification", body: "When the order is on the line we go to the site, follow production and verify the goods before the container is sealed. A QC report with the Insidermark seal reaches you before the container does." },
      { name: "Smart consolidation", body: "Mixed requests from different manufacturers are gathered at one logistics point and shipped in one container. You avoid separate freight and customs for each brand." },
    ],
  },
  consolidation: {
    eyebrow: "Consolidation corridor",
    title: "One container. One invoice. One shipment.",
    body: "Several of our partners sit in the same city. A single collection point in Konya loads multiple sectors onto one truck; the automotive line joins from Ankara and İstanbul.",
    nodes: { konya: "Konya", ankara: "Ankara", istanbul: "İstanbul", cluster: "Konya cluster" },
    ports: { mersin: "Mersin", izmir: "İzmir", ambarli: "Ambarlı" },
    portsNote: { mersin: "East North Africa, Egypt, Libya, Gulf", izmir: "Algeria, Tunisia, Morocco", ambarli: "West and East Africa, ocean" },
    africa: "World",
    manifestTitle: "Consolidated shipment manifest",
    foot: [
      { label: "Mode", value: "Sea freight, FCL / LCL" },
      { label: "Collection", value: "Konya, Ankara, İstanbul" },
      { label: "Ports", value: "Mersin, İzmir, Ambarlı" },
      { label: "Documents", value: "One set, exporter of record" },
    ],
    manifest: [
      { label: "Shipper", value: "Insidermark, exporter of record" },
      { label: "Origin", value: "Konya / Ankara / İstanbul" },
      { label: "Cargo", value: "Mixed industrial, one packing list" },
      { label: "Documents", value: "Proforma, QC report, certificate of origin" },
    ],
  },
  marketsSection: {
    title: "From Türkiye to the world.",
    body: "Milling plants, agricultural and livestock machinery, commercial vehicles and heavy vehicle parts are what growing markets buy most. Türkiye stands between European prices and Far East after-sales gaps. Our network is strongest in North and West Africa, and we work worldwide on request.",
    priorities: [
      { name: "North Africa", countries: "Tunisia, Algeria, Morocco, Libya, Egypt", slug: "algeria" },
      { name: "Sub-Saharan Africa", countries: "Senegal, Ivory Coast, Ghana, Nigeria, Kenya, Ethiopia, Tanzania", slug: "nigeria" },
      { name: "Middle East and Gulf", countries: "Iraq, Jordan, Saudi Arabia, UAE", slug: "algeria" },
      { name: "Central Asia and Caucasus", countries: "Azerbaijan, Uzbekistan, Kazakhstan, Georgia", slug: "algeria" },
      { name: "Europe and the Americas", countries: "Spain, Mexico, the United States", slug: "algeria" },
    ],
    open: "Open market",
  },
  process: {
    title: "How we work",
    intro: "Eight steps, one counterpart, a quotation target of 48 hours.",
    steps: [
      { title: "Request", body: "Your RFQ arrives through the form, an Excel list or a one page PDF. Missing technical data stops the clock." },
      { title: "Factory match", body: "The request is routed to the best fitting partner. Items outside the network are quoted only after a supplier quality check." },
      { title: "Quotation", body: "Price, lead time, Incoterms, payment terms and QC scope in one document. Target: 48 hours." },
      { title: "Compliance", body: "Your country's import requirements card is read; inspections and certificates are priced in." },
      { title: "Contract", body: "Payment terms are fixed in writing. New buyers: advance or confirmed letter of credit." },
      { title: "Production tracking", body: "The line is followed and you receive periodic status. A lead time deviation is reported the moment it appears." },
      { title: "Quality control", body: "Pre-shipment QC report with the Insidermark seal. Mixed orders are consolidated at the Konya collection point." },
      { title: "Shipment", body: "Customs and freight are organised. Installation, training and the spare parts plan start after delivery." },
    ],
    note: "Bad news travels first. A delay, a quality issue or a price change is reported the moment it appears.",
  },
  references: {
    eyebrow: "Network references",
    title: "What the network has already built.",
    items: [
      { title: "150 t/day flour mill in 50 days", where: "Algeria", body: "Ten roller mills, 35 days of production and 15 days of installation. Commissioned by our milling partner and now the closest reference for every North African plant we quote.", image: "/assets/refs/algeria-mill.jpg", facts: ["150 t/day", "10 roller mills", "50 days"] },
      { title: "National tender, tillage fleet", where: "Azerbaijan", body: "Our agricultural partner won the Azerbaijan national tender and made the first Turkish agricultural machinery export to the United States.", image: "/assets/refs/azerbaijan-tillage.jpg", facts: ["National tender", "US export"] },
    ],
    disclaimer: "References belong to our manufacturing partners and are shown with permission. Insidermark's own first shipments are documented as case studies as they complete.",
  },
  closing: { title: "Send the list. We handle the rest.", body: "A quotation within 48 hours, in your language." },
  footer: {
    address: "Konya · İstanbul, Türkiye",
    columns: [
      { title: "Sectors", links: [
        { label: "Automotive & Commercial Vehicles", to: "/divisions/automotive-heavy-duty" },
        { label: "Engineering & Railway", to: "/divisions/engineering-railway" },
        { label: "Industrial Plant Solutions", to: "/divisions/industrial-plants" },
        { label: "Agriculture & Livestock Machinery", to: "/divisions/agriculture-livestock" },
        { label: "Special & Custom Sourcing", to: "/divisions/custom-sourcing" },
      ] },
      { title: "Company", links: [
        { label: "Beyond Sourcing", to: "/beyond-sourcing" },
        { label: "FAQ", to: "/faq" },
        { label: "How we work", to: "/how-we-work" },
        { label: "Manufacturing network", to: "/manufacturing-network" },
        { label: "Markets", to: "/markets" },
        { label: "About", to: "/about" },
        { label: "Contact", to: "/contact" },
      ] },
      { title: "Request a quotation", links: [
        { label: "Spare parts (Excel upload)", to: "/rfq/spare-parts" },
        { label: "Custom manufacturing (drawing)", to: "/rfq/custom-manufacturing" },
        { label: "Turnkey project", to: "/rfq/turnkey-project" },
      ] },
    ],
    legal: "Insidermark | Global Sourcing Partner.",
    privacy: "Privacy", terms: "Terms of use",
    languages: "Language",
  },
  divisions: divisionsEn,
  divisionPage: { products: "Products and services", capacity: "Verified capacity", buyer: "Typical buyer and order", markets: "Fitting markets", constraint: "What we say honestly", rfq: "Request a quotation for this division", back: "All divisions", catalog: "Catalogue", catalogNote: "Catalogues are sent by email on request through the quotation form." },
  markets: marketsEn,
  marketPage: { activeDivisions: "Active divisions in this market", importRules: "Import requirements, summary", importNote: "Regimes change often. We confirm the current scope before every order and manage the inspection on your behalf.", logistics: "Logistics", origin: "Port of loading", destination: "Port of discharge", transit: "Indicative transit", reference: "Nearest reference", language: "Your language", rfq: "Request a quotation for this market", all: "All markets", phase2: "Programme opening in phase two" },
  how: {
    title: "How we work",
    intro: "Three layers sold together, eight steps, and the rules we do not bend.",
    representation: { title: "Authorised partnership", body: [
      "We work as the authorised partner of established Turkish manufacturers. Export requests are routed to us by the manufacturers, and we manage the quotation, production follow-up, verification and shipment as your single counterpart.",
      "You work with one counterpart whatever division you buy from. Internal complexity is ours to manage, not yours to see.",
    ] },
    quality: { title: "Quality control", body: [
      "From order confirmation to loading the production line is followed. Before the container is sealed a QC report with the Insidermark seal is issued to you: photographs, measured items, packing check, marking check.",
      "Many import markets require an independent pre-shipment inspection. If it has to be done anyway, we manage it for you as part of the same step.",
    ], report: [
      { label: "Report", value: "QC-2026-0000 (sample layout)" },
      { label: "Consignment", value: "Mixed container, 3 manufacturers" },
      { label: "Checked", value: "Quantity, marking, packing, dimensions" },
      { label: "Photos", value: "Per line item, before and after packing" },
      { label: "Result", value: "Released for loading" },
    ] },
    consolidation: { title: "Consolidation and logistics", body: [
      "Buying from five factories directly means five contracts, five freights, five customs entries and five lead times to chase. We reduce that to one shipment.",
      "Konya collection point for our industrial partners in the city; automotive items collected on the Ankara to İstanbul line. Ports: Mersin for east North Africa, Egypt, Libya and the Gulf; İzmir for Algeria, Tunisia and Morocco; Ambarlı and Kocaeli for West and East Africa.",
    ] },
    incoterms: { title: "Incoterms, in plain words", intro: "Who pays for what, and where risk changes hands.", terms: [
      { code: "EXW", name: "Ex Works", body: "You collect at the factory gate. Everything after the gate, including export clearance, is yours." },
      { code: "FOB", name: "Free on Board", body: "We deliver the goods on board the vessel at the Turkish port, export cleared. Sea freight and insurance are yours." },
      { code: "CIF", name: "Cost, Insurance and Freight", body: "We pay freight and insurance to your port. Import clearance and inland transport are yours." },
    ] },
    payment: { title: "Payment", body: [
      "New buyers: advance payment or a confirmed letter of credit. Deferred terms are considered only after a trading history, with credit insurance.",
      "Offer validity is kept short, 7 to 15 days, because freight and currency move. Production does not start before payment terms are fixed in writing.",
      "For turnkey projects we structure Türk Eximbank buyer credit and export credit insurance where available. In many markets, financing closes the project more often than price does.",
    ] },
    faq: "A question we always get",
    bypass: { q: "Why not buy directly from the factory?", a: "You can reach them, and many buyers do. But a single, irregular buyer is usually quoted the standard export price and joins the queue, with five contracts, five freights and five lead times to manage. We aim to bring partner-level terms, reduce the work to one shipment, and the pre-shipment QC report is your eyes on the ground." },
  },
  network: {
    title: "Manufacturing network",
    intro: "Five sectors, five production partners. The list is deliberately short: a deep relationship is easier to defend than a long list.",
    partners: [
      { area: "Commercial vehicle spare parts, İstanbul", since: "Since 1978", capacity: "7,000+ delivery points in 81 provinces, 50,000 m2+ warehousing in 7 sites, 60+ brands", certs: "Wholesale distribution for MAN and Mercedes-Benz", division: "automotive-heavy-duty" },
      { area: "Commercial vehicles, Ankara, Bursa, İzmir", since: "Since 1980", capacity: "Authorised dealer and service for MAN, TEMSA, FUSO Canter and ZF; bus and minibus conversion", certs: "Authorised dealer network", division: "automotive-heavy-duty" },
      { area: "Forging, casting and machining, Konya", since: "About 50 years", capacity: "50,000 m2 covered, forging to 200 kg, machining to 30 t, about 1,000 staff, exports to 45 countries", certs: "TSI EN 15566, EN 15085, NDT Level 2, test laboratory", division: "engineering-railway" },
      { area: "Milling machinery, Konya", since: "Since 1994", capacity: "Turnkey flour and semolina plants, silos, automation, after-sales", certs: "Reference: 150 t/day plant in Algeria, 50 days", division: "industrial-plants" },
      { area: "Agricultural machinery, Konya", since: "Since 1980", capacity: "Rotary tillers, vertical cultivators, shredders, inter-row hoes", certs: "Azerbaijan national tender; first Turkish agricultural machinery export to the US", division: "agriculture-livestock" },
    ],
    corridor: "Konya cluster: several partners in one city, one collection point, one truck. Ankara to İstanbul line for automotive. Ports: Mersin, İzmir, Ambarlı and Kocaeli.",
    rule: "We do not link to manufacturers' websites. Capacity, plants and certificates are shown; the relationship is managed by Insidermark.",
  },
  about: {
    title: "About Insidermark",
    vision: { title: "Vision", body: "To be the first address for industrial and agricultural sourcing from Türkiye for buyers worldwide, making Turkish manufacturing as reachable as a procurement office in the buyer's own country." },
    mission: { title: "Mission", body: "To combine the capacity of different manufacturers in a single procurement interface, and to carry the buyer's supply risk with inspected quality, one counterpart and a predictable lead time on every shipment." },
    values: { title: "Values", items: [
      { name: "Verifiable", body: "Everything we say can be proven with a document: certificate, audit report, reference project. Claims without documents are not used." },
      { name: "One counterpart", body: "Whatever division you buy from, one person is responsible." },
      { name: "Clear quotations", body: "Pricing varies by product, volume and scope of shipment. Every quotation states what the price covers: product, quality control, consolidation and logistics." },
      { name: "Bad news first", body: "A delay, a quality issue or a price change is reported the moment it appears." },
      { name: "Long term", body: "Repeat orders over one time margins. Success is the third order, not the first." },
    ] },
    notWeAre: { title: "What we are not", items: ["Not a broker: we stand between manufacturer and buyer for the whole process, from production follow-up to delivery, not for a single transaction.", "Not a price intermediary: we compete on supply security and operations, not on price.", "Not a one time trader: the goal is to be the brand's permanent address in your market."] },
    whyTurkiye: { title: "Why Türkiye", body: [
      "Buyers in many markets cannot pay European prices and do not trust Far East after-sales gaps. Türkiye stands exactly in that range: European standards, short sea legs, and manufacturers who have exported for decades.",
      "Konya alone holds forging, milling and agricultural machinery manufacturing in one city. That geography is what makes a mixed container possible.",
    ] },
    team: { title: "Executive Desk", body: "Every request moves with one accountable counterpart and, when needed, directly with the managing partner. For project-level requests, write to the Executive Desk." },
  },
  contact: {
    title: "Contact",
    body: "Write in English, Turkish or French. Quotations are answered within 48 hours.",
    email: "Email", whatsapp: "WhatsApp", offices: "Offices", hours: "Hours", languagesSpoken: "Languages",
    spokenList: "English, French, Turkish. Arabic on request.",
    hoursValue: "Mon to Fri, 09:00 to 18:00 (UTC+3)",
    desk: { title: "Executive Desk", body: "Direct Desk: Managing Partner. For project-level requests that need a decision maker, this line goes straight to the managing partner, not to a support queue.", cta: "Open the Executive Desk" },
  },
  rfq: {
    title: "Request a quotation",
    intro: "Three flows. Pick the one that matches your need; the form asks only what that need requires.",
    flows: [
      { flow: "spare-parts", title: "Spare parts", body: "Long OEM code lists. Download the Excel template, fill it, upload it. The list is never typed into a browser.", time: "48 hours" },
      { flow: "custom-manufacturing", title: "Custom manufacturing and sourcing", body: "Forging, casting, CNC machining, private label and any machine or part outside our portfolio. Upload drawings or a specification as PDF, DWG or STEP under confidentiality.", time: "3 to 5 days" },
      { flow: "turnkey-project", title: "Turnkey project", body: "Mills, silos, livestock facilities, machinery fleets. A longer brief that ends in a technical consultation call.", time: "Consultation within 5 days" },
    ],
    common: {
      company: "Company", contact: "Contact person", email: "Email", phone: "Phone or WhatsApp", country: "Country", language: "Preferred language",
      port: "Destination port", incoterms: "Incoterms", consolidation: "Consolidation", consolidationHint: "Combine items from several manufacturers in one container",
      notes: "Notes", file: "File", fileHint: "Excel, CSV, PDF, DWG or STEP, up to 25 MB", submit: "Send request", submitting: "Sending",
      privacy: "Uploaded files and drawings are used only to prepare your quotation and are not shared outside the manufacturing partner selected for your request.",
      required: "Required", error: "The request could not be sent. Please try again or write to us directly.",
      consent: "I have read the privacy notice and agree that my data is processed to prepare my quotation.", consentLink: "privacy notice", whatsappContinue: "Continue on WhatsApp", consentError: "Please accept the privacy notice to send your request.",
    },
    spare: { brand: "Vehicle brand and model", brandHint: "MAN TGS, Mercedes-Benz Actros, TEMSA ...", template: "Download the Excel template", templateHint: "Columns: OEM code, description, brand, quantity, target price, vehicle model or chassis.", upload: "Upload your item list" },
    custom: { sector: "Sector", material: "Material specification", quantity: "Quantity", series: "Order type", seriesOptions: ["One time", "Series"], upload: "Upload technical drawing", nda: "Drawings are handled under confidentiality. A signed NDA is available on request before upload." },
    turnkey: {
      plant: "Project type", plantOptions: ["Flour mill", "Semolina or feed plant", "Silo and grain handling", "Livestock facility (milking, feed mixing)", "Machinery fleet", "Other"],
      capacity: "Capacity need", capacityHint: "t/day for plants, head of cattle for livestock facilities", site: "Site status", siteOptions: ["Land only", "Existing building", "Existing plant to modernise", "Not decided"],
      budget: "Budget range", budgetOptions: ["Under 500k USD", "500k to 2M USD", "2M to 10M USD", "Above 10M USD", "Not defined"],
      timeline: "Timeline", timelineOptions: ["Within 6 months", "6 to 12 months", "12 to 24 months", "Exploratory"],
      financing: "Financing", financingOptions: ["Own funds", "Bank financing", "Development bank programme", "Eximbank buyer credit wanted", "Not defined"],
    },
    received: {
      title: "Received. The clock is running.",
      body: "A counterpart replies within 48 hours in your language. If information is missing you will be contacted by email or WhatsApp first.",
      steps: ["Request routed to the fitting division", "Missing data requested, if any", "Quotation with price, lead time, Incoterms, payment and QC scope"],
      ref: "Reference",
      home: "Back to the site",
    },
  },
  languagesNote: "English, Turkish and French are live. Arabic follows in the next phase.",
  a11y: {"keyFigures": "Key figures", "routeDiagram": "Route from factories through one container to the ports and the world", "marketsMap": "Map of active markets", "prevSteps": "Previous steps", "nextSteps": "Next steps", "language": "Language", "primary": "Primary", "chapters": "Chapters"},
  faq: {
    "title": "Frequently asked questions",
    "intro": "Short answers to what buyers ask us most. If your question is not here, write to us or open a quotation request.",
    "items": [
      {
        "q": "What is Insidermark?",
        "a": "Insidermark is a sourcing and operations hub that works with Türkiye's established industrial manufacturers and serves buyers worldwide. We match your request with the right manufacturer, follow production on site, verify the goods before shipment and consolidate orders from different manufacturers into one container."
      },
      {
        "q": "Why work with you instead of buying directly from the factory?",
        "a": "You can reach the factories directly and many buyers do. On top of that we add three things: partner-level terms, production follow-up and verification on site, and one container for orders from several manufacturers, which reduces freight and customs work. You deal with one counterpart."
      },
      {
        "q": "Which manufacturers do you work with?",
        "a": "Our network includes Mapar (commercial vehicles), Hıdırusta (heavy-duty spare parts), FMC (forging, casting, railway, hydraulics) and Özbaşak (milling and plants). For products outside the catalogue our custom sourcing desk finds a suitable manufacturer in Turkish industry."
      },
      {
        "q": "How is the price set?",
        "a": "Pricing varies by product, volume and scope of shipment. Every quotation states what the price covers: product, quality control, consolidation and logistics."
      },
      {
        "q": "How fast will I receive a quotation?",
        "a": "Our target is 48 hours. If technical information is missing the clock stops and we ask you for it. Custom manufacturing takes 3 to 5 days, and turnkey projects get a technical consultation call within 5 days."
      },
      {
        "q": "Is there a minimum order quantity?",
        "a": "The minimum depends on the product and the manufacturer. We handle small and mixed orders through consolidation; send us your request and we will propose a suitable structure."
      },
      {
        "q": "What are the payment terms?",
        "a": "For new buyers: advance payment or a confirmed letter of credit. Deferred terms are considered only after a trading history and with credit insurance. Production does not start before payment terms are fixed in writing."
      },
      {
        "q": "Which Incoterms do you use?",
        "a": "EXW (ex works), FOB (free on board) and CIF (cost, insurance and freight). The quotation is issued under the term that fits your needs."
      },
      {
        "q": "How do you do quality control?",
        "a": "While your order is on the production line we go to the site, follow production and verify the goods before the container is sealed. You receive a report carrying the Insidermark seal: photographs, measured items, packing and marking checks."
      },
      {
        "q": "Is the QC report a guarantee?",
        "a": "The report shows the items checked and the results; its scope is stated in the report. Product warranty is the manufacturer's warranty and its scope is written in the quotation."
      },
      {
        "q": "What is consolidation?",
        "a": "Goods bought from different manufacturers are gathered at one logistics point and shipped in one container, so you avoid separate freight and customs for each brand."
      },
      {
        "q": "Which countries do you serve?",
        "a": "Worldwide. Our network is strongest in Africa (Tunisia, Algeria, Morocco, Libya, Egypt, Senegal, Côte d'Ivoire, Ghana, Nigeria, Kenya, Ethiopia, Tanzania), and we also work on the Middle East, Central Asia, Europe, the Americas and other markets on request."
      },
      {
        "q": "Who handles import documents (SONCAP, PVoC, conformity certificates)?",
        "a": "Your country's current import requirements are checked against official sources before every order. We coordinate mandatory inspections through authorised inspection bodies. Regimes change often, so country information on this site is for guidance only."
      },
      {
        "q": "Do you provide spare parts and after-sales support?",
        "a": "Yes. We have a spare parts and after-sales commitment; its scope and duration are discussed for each product and project and written into the quotation. We stand behind our goods."
      },
      {
        "q": "What is the warranty period?",
        "a": "The warranty period and scope depend on the product and the manufacturer and are stated in the quotation."
      },
      {
        "q": "Can you find a product that is not in a catalogue?",
        "a": "Yes. Our Special & Custom Sourcing desk matches a manufacturer in Turkish heavy industry to your specification, optimises cost and follows production on site to our quality standards. If we cannot identify and verify a qualified manufacturer, we say so in our first answer."
      },
      {
        "q": "Will my drawings and technical files stay confidential?",
        "a": "Uploaded files are used only to prepare your quotation and are not shared outside the selected manufacturing partner. For sensitive drawings we offer a signed NDA on request, and we recommend asking for it before uploading."
      },
      {
        "q": "Which languages do you work in?",
        "a": "Turkish, English and French, with Arabic on request. We plan French support for North and West Africa and Arabic for the Gulf."
      },
      {
        "q": "Do you provide financing?",
        "a": "For suitable projects we review financing structures such as export credit, letters of credit and buyer credit together with you. Terms depend on the project and the country."
      },
      {
        "q": "How long does shipping take?",
        "a": "It depends on the port and the country. Indicatively: Tunisia 4-7 days, Algeria 5-8 days, Morocco 8-12 days, Senegal 18-25 days, Nigeria 20-28 days (from the Turkish port to the port of discharge). The current time is given in the quotation."
      }
    ]
  },
  legal: {
    "updated": "Last updated: October 2026",
    "privacy": {
      "title": "Privacy notice",
      "intro": "This notice explains how personal data submitted through the Insidermark website and quotation forms is processed.",
      "sections": [
        {
          "h": "Controller",
          "p": [
            "The data controller is Insidermark (Konya · İstanbul, Türkiye). Contact: rfq@insidermark.com."
          ]
        },
        {
          "h": "Data we process",
          "p": [
            "The name, company, email, phone, country and request details you enter in the quotation form, and the technical files you upload. Limited technical logs (such as IP address and browser information) may be processed for site security."
          ]
        },
        {
          "h": "Purposes",
          "p": [
            "To assess your request, prepare a quotation, communicate with you, carry out shipment and document processes, and meet legal obligations."
          ]
        },
        {
          "h": "Legal basis",
          "p": [
            "Steps taken at your request before a contract, our legitimate interests and, where required, your consent."
          ]
        },
        {
          "h": "Transfers",
          "p": [
            "Your data may be shared only with manufacturing partners needed for your quotation, logistics and document service providers, and authorities where legally required. Hosting and email services may be provided by providers located abroad; appropriate safeguards apply."
          ]
        },
        {
          "h": "Retention",
          "p": [
            "Your data is kept for as long as the purpose requires and in line with legal retention duties. It is deleted or anonymised on request."
          ]
        },
        {
          "h": "Sensitive files",
          "p": [
            "Before uploading confidential drawings or technical files you can write to us and request a non-disclosure agreement (NDA). For technical data that may be subject to export controls, contact us first."
          ]
        },
        {
          "h": "Your rights",
          "p": [
            "You may request access, correction or deletion of your data, object to processing and learn about transfers. Write to rfq@insidermark.com."
          ]
        },
        {
          "h": "Cookies",
          "p": [
            "The site does not use non-essential cookies by default. If privacy-friendly analytics are used, this page will say so."
          ]
        }
      ]
    },
    "terms": {
      "title": "Terms of use",
      "intro": "By using this site you accept the following terms.",
      "sections": [
        {
          "h": "Information only",
          "p": [
            "Content on this site is for information. Quotations are issued in writing and become binding only through a written contract or an approved proforma."
          ]
        },
        {
          "h": "Country and import information",
          "p": [
            "Import requirements, inspection programmes and transit times change often. Country information on this site does not replace current official sources and is confirmed before every order."
          ]
        },
        {
          "h": "Partner brands",
          "p": [
            "The names and logos of Mapar, Hıdırusta, FMC and Özbaşak belong to their owners and are used to show them as partners."
          ]
        },
        {
          "h": "Intellectual property",
          "p": [
            "The site content, design, the Insidermark name and seal belong to Insidermark and may not be copied without permission."
          ]
        },
        {
          "h": "Liability",
          "p": [
            "The site is provided as is. To the extent permitted by law, no liability is accepted for indirect loss arising from use of the site content."
          ]
        },
        {
          "h": "Governing law",
          "p": [
            "These terms are governed by the laws of the Republic of Türkiye."
          ]
        }
      ]
    }
  },
};

const divisionsFr: Division[] = divisionsEn.map((d) => {
  const fr: Record<DivisionSlug, Partial<Division>> = {
    "automotive-heavy-duty": {
      name: "Automobile et véhicules utilitaires",
      short: "Véhicules utilitaires complets, pièces poids lourds et entrepôt de consolidation.",
      scope: "Export clés en main de bus, tracteurs routiers et véhicules utilitaires spéciaux comme les ambulances, pièces de rechange poids lourds et consolidation de commandes de pièces mixtes dans un seul entrepôt. Points forts : plateformes MAN, Mercedes-Benz et TEMSA.",
      products: [
        { name: "Export de véhicules complets", detail: "Bus, tracteurs routiers, camions et véhicules spéciaux, livrés clés en main pour l'Afrique, le Moyen-Orient et l'Asie centrale" },
        { name: "Pièces de rechange poids lourds", detail: "Des systèmes de freinage à la suspension : moteur, boîte, différentiel, électricité, carrosserie, châssis, remorque et coussins d'air" },
        { name: "Consolidation de pièces", detail: "Demandes de pièces mixtes de plusieurs marques européennes et locales regroupées dans un entrepôt et expédiées sur un seul fret" },
        { name: "Transformation de flottes", detail: "Rénovation et redesign de bus et minibus, remise de flottes" },
      ],
      capacity: [
        { label: "Distribution", value: "7 000+ points de livraison dans 81 provinces" },
        { label: "Entreposage", value: "50 000 m2+ sur 7 sites" },
        { label: "Marques", value: "60+ marques mondiales au catalogue du partenaire" },
        { label: "Export", value: "100+ pays" },
      ],
      buyer: "Exploitants de flottes, importateurs et autorités de transport public disposant de parcs MAN ou Mercedes-Benz. Commande type : 50 à 500 références OEM par envoi.",
      markets: "Afrique, Moyen-Orient et Asie centrale. Les véhicules complets ne sont pas proposés à l'UE ni aux États-Unis (homologation).",
      constraint: "Le marché de la rechange UE et US exige des preuves de marque et de certification par pièce ; nous n'y cotons qu'avec origine documentée.",
    },
    "engineering-railway": {
      name: "Ingénierie avancée et ferroviaire",
      short: "Systèmes ferroviaires, composants pour l'industrie de défense, cylindres hydrauliques, fonderie et CNC.",
      scope: "Systèmes et composants ferroviaires, pièces pour l'industrie de défense, cylindres hydrauliques pour engins lourds, fonderie, forge et usinage CNC sur plan avec vérification qualité Insidermark.",
      products: [
        { name: "Systèmes ferroviaires", detail: "Systèmes de freinage de wagons et composants d'infrastructure sous TSI et soudage EN 15085" },
        { name: "Composants pour l'industrie de défense", detail: "Composants à haute sécurité pour l'industrie de défense" },
        { name: "Cylindres hydrauliques", detail: "Cylindres pour équipements industriels et engins lourds, standard et sur mesure" },
        { name: "Fonderie, forge et CNC", detail: "Réalisés selon votre plan et votre spécification matière, pièces forgées et moulées jusqu'à 200 kg, pièces usinées jusqu'à 30 tonnes" },
        { name: "Marque blanche", detail: "Votre plan, votre marque, la ligne de notre partenaire" },
      ],
      capacity: [
        { label: "Surface couverte", value: "50 000 m2" },
        { label: "Forge et fonderie", value: "jusqu'à 200 kg" },
        { label: "Usinage", value: "jusqu'à 30 t" },
        { label: "Certification", value: "TSI EN 15566, EN 15085, CND niveau 2" },
      ],
      buyer: "OEM et fournisseurs de rang en Europe, Espagne, États-Unis et Mexique qui auditent leurs fournisseurs. Commande type : série à partir d'un plan technique.",
      markets: "Europe, Espagne, États-Unis et Mexique pour l'OEM et la marque blanche ; Afrique du Nord et Moyen-Orient pour le ferroviaire.",
      constraint: "Les composants ferroviaires ne sont cotés qu'avec couverture TSI et EN 15085. Sans certificat, pas d'offre.",
    },
    "agriculture-livestock": {
      name: "Machines agricoles et d'élevage",
      short: "Travail du sol lourd, récolte, mélangeuses d'aliments et traite automatique pour fermes modernes.",
      scope: "Machines pour l'agriculture moderne et l'élevage bovin industriel : travail du sol lourd et récolte, mélangeuses d'aliments et systèmes de traite automatique, livrés de la ligne d'usine à votre site de projet.",
      products: [
        { name: "Travail du sol lourd et récolte", detail: "Équipements pour tracteurs, semoirs pneumatiques, herses à disques, rotavators et machines de récolte lourdes" },
        { name: "Mélangeuses d'aliments", detail: "Mélangeuses industrielles pour fermes bovines" },
        { name: "Systèmes de traite automatique", detail: "Stations de traite entièrement automatiques pour installations laitières" },
        { name: "Technologie pour installations d'élevage", detail: "La couche machines des grands investissements bovins, définie par projet" },
      ],
      capacity: [
        { label: "Partenaire sol depuis", value: "1980, Konya" },
        { label: "Référence", value: "Appel d'offres national, Azerbaïdjan" },
        { label: "Référence", value: "Première exportation turque de machines agricoles vers les États-Unis" },
        { label: "Livraison", value: "De la ligne d'usine à votre site de projet" },
      ],
      buyer: "Exploitations, coopératives, investisseurs laitiers et bovins, ministères et programmes financés par les banques de développement. Commande type : parc de machines ou équipement d'une installation d'élevage.",
      markets: "Monde entier, États-Unis compris.",
      constraint: "Conformité CE et directive machines requise pour l'UE.",
    },
    "industrial-plants": {
      name: "Solutions d'usines industrielles",
      short: "Machines de minoterie et installation d'usines clés en main, des machines aux usines complètes.",
      scope: "Machines de minoterie, semoulerie et aliments du bétail à haute capacité, silos et manutention du grain, et installation d'usines clés en main : nouvelles usines, augmentation de capacité et intégration mécanique gérées de bout en bout.",
      products: [
        { name: "Minoteries clés en main", detail: "De 50 à 500 t/jour, installation, mise en service et formation comprises" },
        { name: "Semoulerie et aliments du bétail", detail: "Lignes blé dur et usines d'aliments composés" },
        { name: "Silos et manutention", detail: "Silos acier, convoyage, réception et chargement" },
        { name: "Installation et modernisation d'usine", detail: "Création d'usine, augmentation de capacité, remplacement de broyeurs, automatisation et intégration mécanique" },
      ],
      capacity: [
        { label: "Référence", value: "150 t/jour, 10 broyeurs, Algérie" },
        { label: "Délai", value: "35 jours de production, 15 jours de montage" },
        { label: "Depuis", value: "1994, Konya" },
        { label: "Service", value: "Montage, formation, plan de pièces de rechange" },
      ],
      buyer: "Investisseurs, minotiers et groupes agroalimentaires. Commande type : usine clés en main avec plan de pièces et montage financier.",
      markets: "Monde entier sur demande, surtout en Afrique.",
      constraint: "Montage, mise en service et formation sont toujours inclus dans le prix. Une usine cotée sans plan de pièces n'est pas une usine.",
    },
    "custom-sourcing": {
      name: "Approvisionnement spécial et sur mesure",
      short: "Notre bureau projet pour les machines et pièces hors portefeuille standard.",
      scope: "Pour les machines et pièces spécifiques absentes de notre portefeuille ou nécessitant une production spéciale, nous parcourons l'écosystème de l'industrie lourde turque, identifions le bon fabricant, optimisons le coût et pilotons la production sur place selon nos propres standards qualité.",
      products: [
        { name: "Sélection du fabricant", detail: "Le bon producteur turc pour votre spécification, choisi dans une base de fournisseurs qualifiés" },
        { name: "Optimisation des coûts", detail: "Devis comparables structurés en une seule offre claire" },
        { name: "Production supervisée", detail: "Suivi sur place de la commande au chargement" },
        { name: "Vérification Insidermark", detail: "Vérification produit avant expédition, avec le même rapport QC que toute autre expédition" },
      ],
      capacity: [
        { label: "Portée", value: "L'écosystème de l'industrie lourde turque" },
        { label: "Processus", value: "Sélectionner, superviser, vérifier" },
        { label: "Bureau", value: "Ligne directe avec l'associé gérant" },
      ],
      buyer: "Acheteurs ayant une machine ou une pièce spécifique qu'aucun catalogue ne couvre. Commande type : un plan ou une spécification avec une quantité cible.",
      markets: "Monde entier sur demande, surtout en Afrique.",
      constraint: "Nous n'acceptons une demande que si un fabricant qualifié peut être identifié et vérifié. Sinon, nous le disons dès la première réponse.",
    },
  };
  return { ...d, ...fr[d.slug] } as Division;
});

const marketsFr: Market[] = marketsEn.map((m) => {
  const names: Record<string, string> = { algeria: "Algérie", tunisia: "Tunisie", morocco: "Maroc", senegal: "Sénégal", nigeria: "Nigéria", libya: "Libye", egypt: "Égypte", "ivory-coast": "Côte d'Ivoire", ghana: "Ghana", kenya: "Kenya", ethiopia: "Éthiopie", tanzania: "Tanzanie" };
  const regions: Record<string, string> = { "North Africa": "Afrique du Nord", "West Africa": "Afrique de l'Ouest", "East Africa": "Afrique de l'Est" };
  const detail: Record<string, Partial<Market>> = {
    algeria: { why: "La référence clés en main la plus forte de notre réseau se trouve en Algérie, et la courte traversée depuis İzmir garde le transit prévisible.", importRules: ["Des programmes d'évaluation de conformité avant expédition s'appliquent à de nombreux groupes de produits ; le régime en vigueur est confirmé avant chaque commande.", "Documents commerciaux en français ; l'arabe est accepté pour la correspondance douanière.", "Nouveaux acheteurs : crédit documentaire confirmé ou paiement d'avance."], logistics: { origin: "İzmir ou Mersin", destination: "Alger, Oran", transit: "5 à 8 jours" }, reference: "Minoterie de 150 t/jour, 10 broyeurs, mise en service en 50 jours (35 jours de production, 15 jours de montage).", language: "Interlocuteur francophone et arabophone." },
    tunisia: { why: "Des relations de terrain et de courtes traversées depuis İzmir font de la Tunisie un marché d'entrée naturel pour minoteries, machines agricoles et pièces de véhicules lourds.", importRules: ["Contrôle technique à l'importation sur listes de produits définies ; vérifié par code SH avant expédition.", "Documentation en français.", "Validité d'offre courte (7 à 15 jours) contre les mouvements de change."], logistics: { origin: "İzmir", destination: "Radès (Tunis), Sfax", transit: "4 à 7 jours" }, reference: "Référence la plus proche : la minoterie clés en main d'Algérie.", language: "Interlocuteur francophone et arabophone." },
    morocco: { why: "La demande de mécanisation agricole est structurelle, et le SIAM figure dans notre calendrier de terrain.", importRules: ["Contrôle de conformité pour les produits industriels réglementés ; périmètre confirmé par commande.", "Documentation en français ; règles d'étiquetage en arabe pour les biens de consommation.", "Le crédit documentaire est la norme pour les premières transactions."], logistics: { origin: "İzmir", destination: "Casablanca, Tanger Med", transit: "8 à 12 jours" }, reference: "Références en machines de travail du sol du pôle de Konya.", language: "Interlocuteur francophone et arabophone." },
    senegal: { why: "Les programmes agricoles et agroalimentaires financés par les banques de développement font du Sénégal notre marché d'entrée en Afrique de l'Ouest.", importRules: ["Les programmes de vérification à l'importation peuvent exiger une inspection avant expédition ; confirmé par commande.", "Documentation en français.", "Le financement décide souvent du projet : crédit acheteur Eximbank et assurance crédit export sont intégrés à l'offre."], logistics: { origin: "Ambarlı ou Kocaeli", destination: "Dakar", transit: "18 à 25 jours" }, reference: "Modèle clés en main éprouvé en Afrique du Nord ; montage et formation compris.", language: "Interlocuteur francophone." },
    nigeria: { why: "Le plus grand parc de véhicules lourds d'Afrique de l'Ouest et un secteur meunier en expansion continue.", importRules: ["L'évaluation de conformité SONCAP est obligatoire pour les produits réglementés ; un conteneur non certifié attend au port et génère des frais de stockage.", "Documentation en anglais.", "Nouveaux acheteurs : crédit documentaire confirmé ou paiement d'avance ; les retards de transfert de devises sont un risque réel, la validité d'offre reste courte."], logistics: { origin: "Ambarlı ou Kocaeli", destination: "Lagos (Apapa, Tin Can)", transit: "20 à 28 jours" }, reference: "Programmes de pièces MAN et Mercedes-Benz de nos partenaires automobiles.", language: "Interlocuteur anglophone." },
  };
  const base = { ...m, name: names[m.slug] ?? m.name, region: regions[m.region] ?? m.region };
  if (m.phase === 2) {
    return { ...base, importRules: ["Régime confirmé par commande."], reference: "Programme en préparation.", language: m.language.replace("English speaking counterpart.", "Interlocuteur anglophone.").replace("Arabic speaking counterpart.", "Interlocuteur arabophone.").replace("Arabic and English speaking counterpart.", "Interlocuteur arabophone et anglophone.").replace("French speaking counterpart.", "Interlocuteur francophone.") };
  }
  return { ...base, ...detail[m.slug] } as Market;
});

const fr: Dictionary = {
  ...en,
  langName: "Français",
  nav: { divisions: "Secteurs", advantage: "Au-delà de l'achat", how: "Notre méthode", markets: "Marchés", network: "Réseau", about: "À propos", contact: "Contact", rfq: "Demander un devis", whatsapp: "WhatsApp", menu: "Menu", close: "Fermer" },
  journey: [
    { id: "origin", label: "Origine", title: "Votre centre intégré d'approvisionnement et d'opérations en Türkiye.", body: "Grâce à des partenariats agréés avec des fabricants et à notre réseau d'approvisionnement sur mesure, nous répondons à vos besoins en industrie lourde depuis un seul centre. Nous suivons vos commandes sur site pendant la production, vérifions les produits avant l'expédition et organisons la consolidation multi-marques de bout en bout.", tags: ["Konya", "Ankara", "İstanbul"] },
    { id: "divisions", label: "Secteurs", title: "Cinq secteurs, un seul interlocuteur.", body: "Véhicules utilitaires, ingénierie et ferroviaire, machines agricoles et d'élevage, usines industrielles et bureau d'approvisionnement sur mesure. Une seule personne responsable.", tags: ["Özbaşak"] },
    { id: "inspection", label: "Inspection", title: "Vérifié sur site avant l'expédition.", body: "Nous suivons la commande sur la ligne de production et vérifions la marchandise avant la fermeture du conteneur. Chaque envoi part avec un rapport de contrôle qualité portant le sceau Insidermark.", tags: ["Mapar", "Hıdırusta"] },
    { id: "consolidation", label: "Consolidation", title: "Un conteneur. Une facture. Une expédition.", body: "Les articles de plusieurs fabricants partent dans un seul conteneur mixte avec un seul jeu de documents d'export.", tags: ["FMC"] },
    { id: "world", label: "Monde", title: "Votre partenaire d'approvisionnement mondial, depuis la Türkiye.", body: "Actif en Afrique, au Moyen-Orient, en Asie centrale, en Europe et dans les Amériques. Un accompagnement en turc, anglais et français, un devis sous 48 heures." },
  ],
  hero: { secondary: "Notre méthode", waText: "Bonjour Insidermark, je souhaite un devis.", slogan: "Votre partenaire d'approvisionnement mondial, depuis la Türkiye." },
  partners: {
    title: "Nos partenaires",
    intro: "Les fabricants derrière chaque expédition. Chaque partenaire représente un secteur de nos cinq.",
    note: "Les noms et marques appartiennent à leurs propriétaires respectifs.",
    items: [
      { name: "Mapar", logoKey: "mapar", sector: "Automobile et véhicules utilitaires", body: "Concessionnaire et service agréés MAN, TEMSA, FUSO Canter et ZF. Export de bus, camions et véhicules spéciaux complets, transformation de bus et minibus.", meta: "Depuis 1980 · Ankara, Bursa, İzmir", division: "automotive-heavy-duty" },
      { name: "Hıdırusta", logoKey: "hidirusta", sector: "Pièces de rechange poids lourds", body: "Distribution de gros de pièces MAN et Mercedes-Benz sur 7 000+ points de livraison dans 81 provinces, 50 000 m2+ d'entrepôt et notre point de consolidation de pièces mixtes.", meta: "Depuis 1978 · İstanbul", division: "automotive-heavy-duty" },
      { name: "FMC", logoKey: "fmc", sector: "Ingénierie avancée et ferroviaire", body: "Forge, fonderie et usinage avec TSI EN 15566, EN 15085 et CND niveau 2. Pièces forgées jusqu'à 200 kg, usinage jusqu'à 30 t, export vers 45 pays.", meta: "Environ 50 ans · Konya", division: "engineering-railway" },
      { name: "Özbaşak", logoKey: "ozbasak", sector: "Solutions d'usines industrielles", body: "Minoteries et semouleries clés en main, silos, automatisation et après-vente. Une minoterie de 150 t/jour en Algérie mise en service en 50 jours.", meta: "Depuis 1994 · Konya", division: "industrial-plants" },
    ],
  },
  engines: {
    title: "Deux moteurs, un seul interlocuteur.",
    intro: "Notre force d'approvisionnement repose sur deux ailes. Utilisez l'une, l'autre, ou les deux dans le même conteneur.",
    items: [
      { tag: "Aile 1", name: "Partenariats agréés", body: "Nous agissons comme bureau d'opérations extérieur de fabricants turcs établis de notre réseau. La force directe des leaders de secteur, par une seule adresse." },
      { tag: "Aile 2", name: "Approvisionnement à la demande et sur mesure", body: "Pour les projets qui ne rentrent pas dans un catalogue, notre bureau projet indépendant vous relie au bon fabricant de l'industrie lourde turque. Ne vous limitez pas à un catalogue : dites-nous le besoin." },
    ],
  },
  advantage: {
    title: "Au-delà de l'achat",
    subtitle: "Là où l'approvisionnement traditionnel s'arrête : pourquoi les acheteurs travaillent avec Insidermark plutôt que d'aller seuls à l'usine.",
    intro: "Dans le commerce international de l'industrie lourde, le plus grand coût n'est pas le produit. Ce sont les opérations non maîtrisées, la production à l'aveugle et la logistique fragmentée. Quand Insidermark s'assoit à la table, nous ne trouvons pas seulement le produit demandé ; nous supprimons les coûts cachés et les risques opérationnels de la chaîne d'approvisionnement.",
    blocks: [
      { n: "01", label: "Position prix", myth: "L'illusion de l'usine directe", mythBody: "Les acheteurs étrangers pensent souvent que joindre directement un grand fabricant donne le prix le plus bas. En pratique, les grandes usines cotent généralement aux acheteurs isolés et irréguliers leur prix export standard.", truth: "Accès de niveau partenaire", truthBody: "Nous travaillons avec nos partenaires industriels comme un interlocuteur récurrent et plus volumineux. Grâce à nos accords et à des achats consolidés, nous visons à vous obtenir des conditions de niveau partenaire et une priorité de production qu'un acheteur ponctuel ne reçoit généralement pas." },
      { n: "02", label: "Processus", myth: "Bureaucratie et logistique fragmentée", mythBody: "Les grands fabricants sont lents. Les spécifications spéciales s'enlisent, les petites commandes mixtes sont refusées, et acheter à plusieurs marques signifie un fret et une douane séparés pour chacune.", truth: "Puissance de consolidation", truthBody: "Nous sommes le bureau d'opérations à l'intérieur du processus, pas un client qui attend à la porte. Nous accélérons les demandes de production spéciale grâce à nos accords, et regroupons les commandes mixtes de différentes marques en un point logistique, dans un conteneur, pour que vous ne payiez plus cinq frets et cinq dédouanements." },
      { n: "03", label: "Risque", myth: "L'angle mort de la qualité", mythBody: "Une fois le paiement parti, le vol est à l'aveugle. Le produit sera-t-il fabriqué dans la tolérance, chargé complet ? Les surprises n'apparaissent qu'à votre douane, sans retour possible.", truth: "Supervision sur site et couverture contractuelle", truthBody: "Nous ne gérons pas depuis un bureau ou par e-mail. Pendant que votre commande est sur la ligne, nous allons sur site, suivons la production et appliquons la vérification QC Insidermark avant le plombage du conteneur. Votre commerce repose sur des accords écrits et sur notre supervision de terrain." },
    ],
    ctaLine: "Connectez-vous à l'industrie lourde turque en partenaire prioritaire, pas en client standard.",
    ctaRfq: "Lancer une demande RFQ B2B",
    ctaDesk: "Bureau exécutif",
  },
  proof: [
    { value: "5", label: "secteurs sous un seul interlocuteur" },
    { value: "5", label: "partenaires industriels, de Konya à İstanbul" },
    { value: "1975", label: "fondation du plus ancien partenaire" },
    { value: "100+", label: "pays déjà servis par nos partenaires" },
  ],
  divisionsSection: { title: "Cinq secteurs clés, un seul interlocuteur.", intro: "Au lieu d'écrire à cinq entreprises en Türkiye, vous travaillez avec une seule. Ouvrez un secteur pour voir périmètre, capacité et marchés.", open: "Ouvrir le secteur" },
  layers: {
    title: "Trois piliers, livrés ensemble.",
    intro: "L'accès seul ferait de nous un courtier. Le deuxième et le troisième pilier sont la partie difficile à remplacer.",
    items: [
      { name: "Prix de niveau partenaire", body: "Notre prix reflète les conditions que nous détenons auprès du fabricant et l'opération qui les garantit. Chaque devis précise ce que le prix couvre, sans coût surprise." },
      { name: "Vérification sur site", body: "Quand la commande est sur la ligne, nous allons sur site, suivons la production et vérifions la marchandise avant le plombage du conteneur. Un rapport QC avec le sceau Insidermark vous parvient avant le conteneur." },
      { name: "Consolidation intelligente", body: "Les demandes mixtes de différents fabricants sont réunies en un point logistique et expédiées dans un conteneur. Vous évitez un fret et une douane séparés par marque." },
    ],
  },
  consolidation: {
    eyebrow: "Corridor de consolidation",
    title: "Un conteneur. Une facture. Une expédition.",
    body: "Plusieurs de nos partenaires sont dans la même ville. Un point de collecte unique à Konya charge plusieurs secteurs sur un camion ; la ligne automobile rejoint depuis Ankara et İstanbul.",
    nodes: { konya: "Konya", ankara: "Ankara", istanbul: "İstanbul", cluster: "Pôle de Konya" },
    ports: { mersin: "Mersin", izmir: "İzmir", ambarli: "Ambarlı" },
    portsNote: { mersin: "Est de l'Afrique du Nord, Égypte, Libye, Golfe", izmir: "Algérie, Tunisie, Maroc", ambarli: "Afrique de l'Ouest et de l'Est, océan" },
    africa: "Monde",
    manifestTitle: "Manifeste d'expédition consolidée",
    foot: [
      { label: "Mode", value: "Fret maritime, FCL / LCL" },
      { label: "Collecte", value: "Konya, Ankara, İstanbul" },
      { label: "Ports", value: "Mersin, İzmir, Ambarlı" },
      { label: "Documents", value: "Un seul jeu, exportateur officiel" },
    ],
    manifest: [
      { label: "Expéditeur", value: "Insidermark, exportateur officiel" },
      { label: "Origine", value: "Konya / Ankara / İstanbul" },
      { label: "Cargaison", value: "Industriel mixte, une liste de colisage" },
      { label: "Documents", value: "Proforma, rapport QC, certificat d'origine" },
    ],
  },
  marketsSection: {
    title: "De la Türkiye au monde.",
    body: "Minoteries, machines agricoles et d'élevage, véhicules utilitaires et pièces de véhicules lourds : ce que les marchés en croissance achètent le plus. La Türkiye se situe entre les prix européens et les lacunes d'après-vente d'Extrême-Orient. Notre réseau est le plus solide en Afrique du Nord et de l'Ouest, et nous travaillons dans le monde entier sur demande.",
    priorities: [
      { name: "Afrique du Nord", countries: "Tunisie, Algérie, Maroc, Libye, Égypte", slug: "algeria" },
      { name: "Afrique subsaharienne", countries: "Sénégal, Côte d'Ivoire, Ghana, Nigéria, Kenya, Éthiopie, Tanzanie", slug: "nigeria" },
      { name: "Moyen-Orient et Golfe", countries: "Irak, Jordanie, Arabie saoudite, EAU", slug: "algeria" },
      { name: "Asie centrale et Caucase", countries: "Azerbaïdjan, Ouzbékistan, Kazakhstan, Géorgie", slug: "algeria" },
      { name: "Europe et Amériques", countries: "Espagne, Mexique, États-Unis", slug: "algeria" },
    ],
    open: "Ouvrir le marché",
  },
  process: {
    title: "Notre méthode",
    intro: "Huit étapes, un interlocuteur, un devis visé sous 48 heures.",
    steps: [
      { title: "Demande", body: "Votre demande arrive par le formulaire, une liste Excel ou un PDF d'une page. Les données techniques manquantes arrêtent le chronomètre." },
      { title: "Choix de l'usine", body: "La demande est orientée vers le partenaire le plus adapté. Les articles hors réseau ne sont cotés qu'après contrôle qualité du fournisseur." },
      { title: "Devis", body: "Prix, délai, Incoterms, conditions de paiement et périmètre QC dans un seul document. Objectif : 48 heures." },
      { title: "Conformité", body: "La fiche d'exigences d'importation de votre pays est lue ; inspections et certificats sont intégrés au prix." },
      { title: "Contrat", body: "Les conditions de paiement sont fixées par écrit. Nouveaux acheteurs : acompte ou crédit documentaire confirmé." },
      { title: "Suivi de production", body: "La ligne est suivie et vous recevez un état périodique. Tout écart de délai est signalé dès qu'il apparaît." },
      { title: "Contrôle qualité", body: "Rapport QC avant expédition avec le sceau Insidermark. Les commandes mixtes sont consolidées au point de collecte de Konya." },
      { title: "Expédition", body: "Douane et fret sont organisés. Montage, formation et plan de pièces démarrent après livraison." },
    ],
    note: "La mauvaise nouvelle voyage en premier. Un retard, un problème qualité ou un changement de prix est signalé dès qu'il apparaît.",
  },
  references: {
    eyebrow: "Références du réseau",
    title: "Ce que le réseau a déjà construit.",
    items: [
      { title: "Minoterie de 150 t/jour en 50 jours", where: "Algérie", body: "Dix broyeurs, 35 jours de production et 15 jours de montage. Mise en service par notre partenaire meunier, aujourd'hui la référence la plus proche pour chaque usine que nous cotons en Afrique du Nord.", image: "/assets/refs/algeria-mill.jpg", facts: ["150 t/jour", "10 broyeurs", "50 jours"] },
      { title: "Appel d'offres national, parc de travail du sol", where: "Azerbaïdjan", body: "Notre partenaire agricole a remporté l'appel d'offres national d'Azerbaïdjan et réalisé la première exportation turque de machines agricoles vers les États-Unis.", image: "/assets/refs/azerbaijan-tillage.jpg", facts: ["Appel d'offres national", "Export États-Unis"] },
    ],
    disclaimer: "Les références appartiennent à nos partenaires industriels et sont présentées avec leur accord. Les premières expéditions d'Insidermark sont documentées en études de cas à mesure qu'elles s'achèvent.",
  },
  closing: { title: "Envoyez la liste. Nous faisons le reste.", body: "Un devis sous 48 heures, dans votre langue." },
  footer: {
    address: "Konya · İstanbul, Türkiye",
    columns: [
      { title: "Secteurs", links: [
        { label: "Automobile et véhicules utilitaires", to: "/divisions/automotive-heavy-duty" },
        { label: "Ingénierie et ferroviaire", to: "/divisions/engineering-railway" },
        { label: "Solutions d'usines industrielles", to: "/divisions/industrial-plants" },
        { label: "Machines agricoles et d'élevage", to: "/divisions/agriculture-livestock" },
        { label: "Approvisionnement spécial", to: "/divisions/custom-sourcing" },
      ] },
      { title: "Société", links: [
        { label: "Au-delà de l'achat", to: "/beyond-sourcing" },
        { label: "FAQ", to: "/faq" },
        { label: "Notre méthode", to: "/how-we-work" },
        { label: "Réseau industriel", to: "/manufacturing-network" },
        { label: "Marchés", to: "/markets" },
        { label: "À propos", to: "/about" },
        { label: "Contact", to: "/contact" },
      ] },
      { title: "Demander un devis", links: [
        { label: "Pièces de rechange (Excel)", to: "/rfq/spare-parts" },
        { label: "Fabrication sur plan", to: "/rfq/custom-manufacturing" },
        { label: "Projet clés en main", to: "/rfq/turnkey-project" },
      ] },
    ],
    legal: "Insidermark | Global Sourcing Partner.",
    privacy: "Confidentialité", terms: "Conditions d'utilisation",
    languages: "Langue",
  },
  divisions: divisionsFr,
  divisionPage: { products: "Produits et services", capacity: "Capacité vérifiée", buyer: "Acheteur et commande types", markets: "Marchés adaptés", constraint: "Ce que nous disons franchement", rfq: "Demander un devis pour cette division", back: "Toutes les divisions", catalog: "Catalogue", catalogNote: "Les catalogues sont envoyés par e-mail sur demande via le formulaire de devis." },
  markets: marketsFr,
  marketPage: { activeDivisions: "Divisions actives sur ce marché", importRules: "Exigences d'importation, résumé", importNote: "Les régimes changent souvent. Nous confirmons le périmètre en vigueur avant chaque commande et gérons l'inspection pour vous.", logistics: "Logistique", origin: "Port de chargement", destination: "Port de déchargement", transit: "Transit indicatif", reference: "Référence la plus proche", language: "Votre langue", rfq: "Demander un devis pour ce marché", all: "Tous les marchés", phase2: "Programme ouvert en phase deux" },
  how: {
    title: "Notre méthode",
    intro: "Trois couches vendues ensemble, huit étapes, et les règles que nous ne plions pas.",
    representation: { title: "Partenariat agréé", body: [
      "Nous travaillons comme partenaire agréé de fabricants turcs établis. Les demandes export nous sont orientées par les fabricants, et nous gérons le devis, le suivi de production, la vérification et l'expédition en tant que votre interlocuteur unique.",
      "Vous travaillez avec un seul interlocuteur quelle que soit la division. La complexité interne nous appartient.",
    ] },
    quality: { title: "Contrôle qualité", body: [
      "De la confirmation de commande au chargement, la ligne de production est suivie. Avant le plombage du conteneur, un rapport QC portant le sceau Insidermark vous est remis : photographies, articles mesurés, contrôle d'emballage, contrôle de marquage.",
      "De nombreux marchés d'importation exigent une inspection indépendante avant expédition. Si elle doit être faite, nous la gérons pour vous dans la même étape.",
    ], report: [
      { label: "Rapport", value: "QC-2026-0000 (mise en page type)" },
      { label: "Envoi", value: "Conteneur mixte, 3 fabricants" },
      { label: "Contrôlé", value: "Quantité, marquage, emballage, dimensions" },
      { label: "Photos", value: "Par article, avant et après emballage" },
      { label: "Résultat", value: "Libéré pour chargement" },
    ] },
    consolidation: { title: "Consolidation et logistique", body: [
      "Acheter directement à cinq usines, c'est cinq contrats, cinq frets, cinq déclarations en douane et cinq délais à suivre. Nous ramenons cela à une expédition.",
      "Point de collecte de Konya pour nos partenaires industriels de la ville ; articles automobiles collectés sur la ligne Ankara İstanbul. Ports : Mersin pour l'est de l'Afrique du Nord, l'Égypte, la Libye et le Golfe ; İzmir pour l'Algérie, la Tunisie et le Maroc ; Ambarlı et Kocaeli pour l'Afrique de l'Ouest et de l'Est.",
    ] },
    incoterms: { title: "Incoterms, en clair", intro: "Qui paie quoi, et où le risque change de mains.", terms: [
      { code: "EXW", name: "À l'usine", body: "Vous enlevez à la porte de l'usine. Tout ce qui suit, y compris le dédouanement export, vous appartient." },
      { code: "FOB", name: "Franco à bord", body: "Nous livrons la marchandise à bord du navire au port turc, dédouanée à l'export. Fret maritime et assurance vous appartiennent." },
      { code: "CIF", name: "Coût, assurance et fret", body: "Nous payons fret et assurance jusqu'à votre port. Dédouanement import et transport intérieur vous appartiennent." },
    ] },
    payment: { title: "Paiement", body: [
      "Nouveaux acheteurs : paiement d'avance ou crédit documentaire confirmé. Les délais de paiement ne sont envisagés qu'après un historique commercial, avec assurance crédit.",
      "La validité d'offre reste courte, 7 à 15 jours, car fret et devises bougent. La production ne démarre pas avant fixation écrite des conditions de paiement.",
      "Pour les projets clés en main nous structurons le crédit acheteur Türk Eximbank et l'assurance crédit export lorsqu'ils sont disponibles. Sur de nombreux marchés, le financement conclut le projet plus souvent que le prix.",
    ] },
    faq: "Une question que l'on nous pose toujours",
    bypass: { q: "Pourquoi ne pas acheter directement à l'usine ?", a: "Vous pouvez les joindre, et beaucoup d'acheteurs le font. Mais un acheteur isolé et irrégulier se voit généralement coter le prix export standard et entre dans la file, avec cinq contrats, cinq frets et cinq délais à gérer. Nous visons à obtenir des conditions de niveau partenaire, à ramener le travail à une expédition, et le rapport QC avant expédition est votre regard sur place." },
  },
  network: {
    title: "Réseau industriel",
    intro: "Cinq secteurs, cinq partenaires de production. La liste est volontairement courte : une relation profonde se défend mieux qu'une longue liste.",
    partners: [
      { area: "Pièces de véhicules utilitaires, İstanbul", since: "Depuis 1978", capacity: "7 000+ points de livraison dans 81 provinces, 50 000 m2+ d'entrepôt sur 7 sites, 60+ marques", certs: "Distribution de gros MAN et Mercedes-Benz", division: "automotive-heavy-duty" },
      { area: "Véhicules utilitaires, Ankara, Bursa, İzmir", since: "Depuis 1980", capacity: "Concessionnaire et service agréés MAN, TEMSA, FUSO Canter et ZF ; transformation de bus et minibus", certs: "Réseau de concessionnaires agréés", division: "automotive-heavy-duty" },
      { area: "Forge, fonderie et usinage, Konya", since: "Environ 50 ans", capacity: "50 000 m2 couverts, forge jusqu'à 200 kg, usinage jusqu'à 30 t, environ 1 000 salariés, export vers 45 pays", certs: "TSI EN 15566, EN 15085, CND niveau 2, laboratoire d'essais", division: "engineering-railway" },
      { area: "Machines de minoterie, Konya", since: "Depuis 1994", capacity: "Minoteries et semouleries clés en main, silos, automatisation, après-vente", certs: "Référence : usine de 150 t/jour en Algérie, 50 jours", division: "industrial-plants" },
      { area: "Machines agricoles, Konya", since: "Depuis 1980", capacity: "Rotavators, fraises verticales, broyeurs, bineuses", certs: "Appel d'offres national d'Azerbaïdjan ; première exportation turque de machines agricoles vers les États-Unis", division: "agriculture-livestock" },
    ],
    corridor: "Pôle de Konya : plusieurs partenaires dans une ville, un point de collecte, un camion. Ligne Ankara İstanbul pour l'automobile. Ports : Mersin, İzmir, Ambarlı et Kocaeli.",
    rule: "Nous ne renvoyons pas vers les sites des fabricants. Capacité, usines et certificats sont montrés ; la relation est gérée par Insidermark.",
  },
  about: {
    title: "À propos d'Insidermark",
    vision: { title: "Vision", body: "Être la première adresse d'approvisionnement industriel et agricole depuis la Türkiye pour les acheteurs du monde entier, en rendant la production turque aussi accessible qu'un bureau d'achat dans le pays de l'acheteur." },
    mission: { title: "Mission", body: "Réunir la capacité de différents fabricants dans une seule interface d'achat, et porter le risque d'approvisionnement de l'acheteur avec une qualité inspectée, un interlocuteur unique et un délai prévisible à chaque expédition." },
    values: { title: "Valeurs", items: [
      { name: "Vérifiable", body: "Tout ce que nous disons peut être prouvé par un document : certificat, rapport d'audit, projet de référence." },
      { name: "Un interlocuteur", body: "Quelle que soit la division, une seule personne est responsable." },
      { name: "Devis clairs", body: "Le prix varie selon le produit, le volume et le périmètre de l'envoi. Chaque devis précise ce que le prix couvre : produit, contrôle qualité, consolidation et logistique." },
      { name: "La mauvaise nouvelle d'abord", body: "Un retard, un problème qualité ou un changement de prix est signalé dès qu'il apparaît." },
      { name: "Long terme", body: "Les commandes répétées avant les marges ponctuelles. Le succès, c'est la troisième commande." },
    ] },
    notWeAre: { title: "Ce que nous ne sommes pas", items: ["Pas un courtier : nous nous tenons entre le fabricant et l'acheteur sur tout le processus, du suivi de production à la livraison, et non pour une seule transaction.", "Pas un intermédiaire de prix : nous rivalisons sur la sécurité d'approvisionnement et les opérations.", "Pas un négociant ponctuel : l'objectif est d'être l'adresse permanente de la marque sur votre marché."] },
    whyTurkiye: { title: "Pourquoi la Türkiye", body: [
      "Les acheteurs de nombreux marchés ne peuvent pas payer les prix européens et ne font pas confiance aux lacunes d'après-vente d'Extrême-Orient. La Türkiye se situe exactement dans cet intervalle : normes européennes, traversées courtes, fabricants exportateurs depuis des décennies.",
      "Konya réunit à elle seule forge, minoterie et machines agricoles dans une même ville. C'est cette géographie qui rend le conteneur mixte possible.",
    ] },
    team: { title: "Bureau exécutif", body: "Chaque demande avance avec un interlocuteur responsable et, si nécessaire, directement avec l'associé gérant. Pour les demandes de projet, écrivez au bureau exécutif." },
  },
  contact: {
    title: "Contact",
    body: "Écrivez en français, en turc ou en anglais. Les devis sont répondus sous 48 heures.",
    email: "E-mail", whatsapp: "WhatsApp", offices: "Bureaux", hours: "Horaires", languagesSpoken: "Langues",
    spokenList: "Français, anglais, turc. Arabe sur demande.",
    hoursValue: "Lun au ven, 09h00 à 18h00 (UTC+3)",
    desk: { title: "Bureau exécutif", body: "Ligne directe : associé gérant. Pour les demandes de projet qui exigent un décideur, cette ligne va droit à l'associé gérant, pas à une file de support.", cta: "Ouvrir le bureau exécutif" },
  },
  rfq: {
    title: "Demander un devis",
    intro: "Trois parcours. Choisissez celui qui correspond à votre besoin ; le formulaire ne demande que ce que ce besoin exige.",
    flows: [
      { flow: "spare-parts", title: "Pièces de rechange", body: "Longues listes de codes OEM. Téléchargez le modèle Excel, remplissez-le, envoyez-le. La liste n'est jamais saisie dans un navigateur.", time: "48 heures" },
      { flow: "custom-manufacturing", title: "Fabrication et approvisionnement sur mesure", body: "Forge, fonderie, usinage CNC, marque blanche et toute machine ou pièce hors portefeuille. Envoyez plans ou spécification en PDF, DWG ou STEP sous confidentialité.", time: "3 à 5 jours" },
      { flow: "turnkey-project", title: "Projet clés en main", body: "Minoteries, silos, installations d'élevage, parcs de machines. Un brief plus long qui se termine par un entretien technique.", time: "Entretien sous 5 jours" },
    ],
    common: {
      company: "Société", contact: "Personne de contact", email: "E-mail", phone: "Téléphone ou WhatsApp", country: "Pays", language: "Langue préférée",
      port: "Port de destination", incoterms: "Incoterms", consolidation: "Consolidation", consolidationHint: "Regrouper les articles de plusieurs fabricants dans un conteneur",
      notes: "Remarques", file: "Fichier", fileHint: "Excel, CSV, PDF, DWG ou STEP, jusqu'à 25 Mo", submit: "Envoyer la demande", submitting: "Envoi",
      privacy: "Les fichiers et plans envoyés servent uniquement à préparer votre devis et ne sont pas partagés en dehors du partenaire industriel retenu pour votre demande.",
      required: "Obligatoire", error: "La demande n'a pas pu être envoyée. Réessayez ou écrivez-nous directement.",
      consent: "J'ai lu la notice de confidentialité et j'accepte que mes données soient traitées pour préparer mon devis.", consentLink: "notice de confidentialité", whatsappContinue: "Continuer sur WhatsApp", consentError: "Veuillez accepter la notice de confidentialité pour envoyer votre demande.",
    },
    spare: { brand: "Marque et modèle du véhicule", brandHint: "MAN TGS, Mercedes-Benz Actros, TEMSA ...", template: "Télécharger le modèle Excel", templateHint: "Colonnes : code OEM, désignation, marque, quantité, prix cible, modèle ou châssis.", upload: "Envoyer votre liste d'articles" },
    custom: { sector: "Secteur", material: "Spécification matière", quantity: "Quantité", series: "Type de commande", seriesOptions: ["Ponctuelle", "Série"], upload: "Envoyer le plan technique", nda: "Les plans sont traités sous confidentialité. Un accord de confidentialité signé est disponible sur demande avant envoi." },
    turnkey: {
      plant: "Type de projet", plantOptions: ["Minoterie", "Semoulerie ou aliments du bétail", "Silos et manutention", "Installation d'élevage (traite, mélange d'aliments)", "Parc de machines", "Autre"],
      capacity: "Capacité", capacityHint: "t/jour pour les usines, nombre de bovins pour les installations d'élevage", site: "État du site", siteOptions: ["Terrain seul", "Bâtiment existant", "Usine à moderniser", "Non décidé"],
      budget: "Budget", budgetOptions: ["Moins de 500 k USD", "500 k à 2 M USD", "2 M à 10 M USD", "Plus de 10 M USD", "Non défini"],
      timeline: "Calendrier", timelineOptions: ["Sous 6 mois", "6 à 12 mois", "12 à 24 mois", "Exploratoire"],
      financing: "Financement", financingOptions: ["Fonds propres", "Financement bancaire", "Programme de banque de développement", "Crédit acheteur Eximbank souhaité", "Non défini"],
    },
    received: {
      title: "Reçu. Le chronomètre tourne.",
      body: "Un interlocuteur répond sous 48 heures dans votre langue. S'il manque des informations, vous serez d'abord contacté par e-mail ou WhatsApp.",
      steps: ["Demande orientée vers la division adaptée", "Données manquantes demandées, le cas échéant", "Devis avec prix, délai, Incoterms, paiement et périmètre QC"],
      ref: "Référence",
      home: "Retour au site",
    },
  },
  languagesNote: "L'anglais, le turc et le français sont en ligne. L'arabe suit dans la prochaine phase.",
  a11y: {"keyFigures": "Chiffres clés", "routeDiagram": "Itinéraire des usines, par un seul conteneur, vers les ports et le monde", "marketsMap": "Carte des marchés actifs", "prevSteps": "Étapes précédentes", "nextSteps": "Étapes suivantes", "language": "Langue", "primary": "Principale", "chapters": "Chapitres"},
  faq: {
    "title": "Questions fréquentes",
    "intro": "Des réponses courtes aux questions que les acheteurs nous posent le plus. Si la vôtre n'y figure pas, écrivez-nous ou ouvrez une demande de devis.",
    "items": [
      {
        "q": "Qu'est-ce qu'Insidermark ?",
        "a": "Insidermark est un centre d'approvisionnement et d'opérations qui travaille avec des fabricants industriels turcs établis et sert des acheteurs dans le monde entier. Nous associons votre demande au bon fabricant, suivons la production sur site, vérifions la marchandise avant l'expédition et regroupons les commandes de plusieurs fabricants dans un seul conteneur."
      },
      {
        "q": "Pourquoi travailler avec vous plutôt qu'acheter directement à l'usine ?",
        "a": "Vous pouvez joindre les usines directement et beaucoup d'acheteurs le font. Nous ajoutons trois choses : des conditions de niveau partenaire, un suivi de production et une vérification sur site, et un seul conteneur pour plusieurs fabricants, ce qui réduit fret et formalités douanières. Vous travaillez avec un seul interlocuteur."
      },
      {
        "q": "Avec quels fabricants travaillez-vous ?",
        "a": "Notre réseau comprend Mapar (véhicules utilitaires), Hıdırusta (pièces de rechange poids lourds), FMC (forge, fonderie, ferroviaire, hydraulique) et Özbaşak (minoterie et usines). Pour les produits hors catalogue, notre bureau d'approvisionnement sur mesure trouve un fabricant adapté dans l'industrie turque."
      },
      {
        "q": "Comment le prix est-il établi ?",
        "a": "Le prix varie selon le produit, le volume et le périmètre de l'envoi. Chaque devis précise ce que le prix couvre : produit, contrôle qualité, consolidation et logistique."
      },
      {
        "q": "Sous quel délai recevrai-je un devis ?",
        "a": "Notre objectif est de 48 heures. Si des informations techniques manquent, le chronomètre s'arrête et nous vous les demandons. La fabrication sur plan prend 3 à 5 jours, et les projets clés en main ont un entretien technique sous 5 jours."
      },
      {
        "q": "Y a-t-il une quantité minimale de commande ?",
        "a": "Le minimum dépend du produit et du fabricant. Nous traitons les commandes petites et mixtes par la consolidation ; envoyez votre demande et nous proposerons la structure adaptée."
      },
      {
        "q": "Quelles sont les conditions de paiement ?",
        "a": "Pour les nouveaux acheteurs : paiement d'avance ou crédit documentaire confirmé. Les délais de paiement ne sont envisagés qu'après un historique commercial et avec une assurance crédit. La production ne démarre pas avant fixation écrite des conditions de paiement."
      },
      {
        "q": "Quels Incoterms utilisez-vous ?",
        "a": "EXW (à l'usine), FOB (franco à bord) et CIF (coût, assurance et fret). Le devis est établi selon la condition adaptée à votre besoin."
      },
      {
        "q": "Comment faites-vous le contrôle qualité ?",
        "a": "Pendant que votre commande est sur la ligne de production, nous allons sur site, suivons la production et vérifions la marchandise avant le plombage du conteneur. Vous recevez un rapport portant le sceau Insidermark : photographies, articles mesurés, contrôles d'emballage et de marquage."
      },
      {
        "q": "Le rapport QC est-il une garantie ?",
        "a": "Le rapport indique les articles contrôlés et les résultats ; son périmètre y figure. La garantie produit est celle du fabricant et son périmètre est écrit dans le devis."
      },
      {
        "q": "Qu'est-ce que la consolidation ?",
        "a": "Les marchandises achetées auprès de plusieurs fabricants sont réunies en un point logistique et expédiées dans un seul conteneur, afin d'éviter un fret et une douane séparés par marque."
      },
      {
        "q": "Quels pays servez-vous ?",
        "a": "Dans le monde entier. Notre réseau est le plus solide en Afrique (Tunisie, Algérie, Maroc, Libye, Égypte, Sénégal, Côte d'Ivoire, Ghana, Nigéria, Kenya, Éthiopie, Tanzanie), et nous travaillons aussi sur le Moyen-Orient, l'Asie centrale, l'Europe, les Amériques et d'autres marchés sur demande."
      },
      {
        "q": "Qui gère les documents d'importation (SONCAP, PVoC, certificats de conformité) ?",
        "a": "Les exigences d'importation en vigueur dans votre pays sont vérifiées auprès de sources officielles avant chaque commande. Nous coordonnons les inspections obligatoires par l'intermédiaire d'organismes agréés. Les régimes changent souvent : les informations par pays de ce site sont données à titre indicatif."
      },
      {
        "q": "Proposez-vous des pièces de rechange et un service après-vente ?",
        "a": "Oui. Nous avons un engagement pièces de rechange et après-vente ; son périmètre et sa durée sont discutés pour chaque produit et projet et inscrits dans le devis. Nous sommes derrière notre marchandise."
      },
      {
        "q": "Quelle est la durée de la garantie ?",
        "a": "La durée et le périmètre de la garantie dépendent du produit et du fabricant et figurent dans le devis."
      },
      {
        "q": "Pouvez-vous trouver un produit absent des catalogues ?",
        "a": "Oui. Notre bureau d'approvisionnement spécial et sur mesure associe un fabricant de l'industrie lourde turque à votre spécification, optimise le coût et suit la production sur site selon nos standards qualité. Si nous ne pouvons pas identifier et vérifier un fabricant qualifié, nous le disons dès la première réponse."
      },
      {
        "q": "Mes plans et fichiers techniques resteront-ils confidentiels ?",
        "a": "Les fichiers envoyés servent uniquement à préparer votre devis et ne sont pas partagés en dehors du partenaire industriel retenu. Pour les plans sensibles, nous proposons un accord de confidentialité signé sur demande ; nous recommandons de le demander avant l'envoi."
      },
      {
        "q": "Dans quelles langues travaillez-vous ?",
        "a": "Turc, anglais et français, arabe sur demande. Nous prévoyons le français pour l'Afrique du Nord et de l'Ouest et l'arabe pour le Golfe."
      },
      {
        "q": "Proposez-vous un financement ?",
        "a": "Pour les projets adaptés, nous examinons avec vous des structures de financement comme le crédit export, le crédit documentaire et le crédit acheteur. Les conditions dépendent du projet et du pays."
      },
      {
        "q": "Combien de temps dure l'expédition ?",
        "a": "Cela dépend du port et du pays. À titre indicatif : Tunisie 4-7 jours, Algérie 5-8 jours, Maroc 8-12 jours, Sénégal 18-25 jours, Nigéria 20-28 jours (du port turc au port de déchargement). Le délai actuel figure dans le devis."
      }
    ]
  },
  legal: {
    "updated": "Dernière mise à jour : octobre 2026",
    "privacy": {
      "title": "Notice de confidentialité",
      "intro": "Cette notice explique comment sont traitées les données personnelles transmises via le site Insidermark et les formulaires de devis.",
      "sections": [
        {
          "h": "Responsable du traitement",
          "p": [
            "Le responsable du traitement est Insidermark (Konya · İstanbul, Türkiye). Contact : rfq@insidermark.com."
          ]
        },
        {
          "h": "Données traitées",
          "p": [
            "Le nom, la société, l'e-mail, le téléphone, le pays et les détails de la demande saisis dans le formulaire de devis, ainsi que les fichiers techniques envoyés. Des journaux techniques limités (adresse IP, navigateur) peuvent être traités pour la sécurité du site."
          ]
        },
        {
          "h": "Finalités",
          "p": [
            "Évaluer votre demande, préparer un devis, vous contacter, mener les processus d'expédition et de documents et respecter les obligations légales."
          ]
        },
        {
          "h": "Base juridique",
          "p": [
            "Mesures précontractuelles prises à votre demande, nos intérêts légitimes et, lorsque nécessaire, votre consentement."
          ]
        },
        {
          "h": "Transferts",
          "p": [
            "Vos données ne peuvent être partagées qu'avec les partenaires industriels nécessaires à votre devis, les prestataires logistiques et documentaires, et les autorités lorsque la loi l'exige. L'hébergement et la messagerie peuvent être assurés par des prestataires situés à l'étranger ; des garanties appropriées s'appliquent."
          ]
        },
        {
          "h": "Conservation",
          "p": [
            "Vos données sont conservées aussi longtemps que la finalité l'exige et conformément aux obligations légales. Elles sont supprimées ou anonymisées sur demande."
          ]
        },
        {
          "h": "Fichiers sensibles",
          "p": [
            "Avant d'envoyer des plans ou fichiers techniques confidentiels, vous pouvez nous écrire pour demander un accord de confidentialité (NDA). Pour des données techniques pouvant relever du contrôle des exportations, contactez-nous d'abord."
          ]
        },
        {
          "h": "Vos droits",
          "p": [
            "Vous pouvez demander l'accès, la rectification ou la suppression de vos données, vous opposer au traitement et connaître les transferts. Écrivez à rfq@insidermark.com."
          ]
        },
        {
          "h": "Cookies",
          "p": [
            "Le site n'utilise pas de cookies non essentiels par défaut. Si des statistiques respectueuses de la vie privée sont utilisées, cette page l'indiquera."
          ]
        }
      ]
    },
    "terms": {
      "title": "Conditions d'utilisation",
      "intro": "En utilisant ce site, vous acceptez les conditions suivantes.",
      "sections": [
        {
          "h": "Information uniquement",
          "p": [
            "Le contenu de ce site est donné à titre d'information. Les devis sont établis par écrit et ne deviennent contraignants que par un contrat écrit ou une proforma approuvée."
          ]
        },
        {
          "h": "Informations pays et importation",
          "p": [
            "Les exigences d'importation, programmes d'inspection et délais changent souvent. Les informations par pays de ce site ne remplacent pas les sources officielles en vigueur et sont confirmées avant chaque commande."
          ]
        },
        {
          "h": "Marques partenaires",
          "p": [
            "Les noms et logos de Mapar, Hıdırusta, FMC et Özbaşak appartiennent à leurs propriétaires et sont utilisés pour les présenter comme partenaires."
          ]
        },
        {
          "h": "Propriété intellectuelle",
          "p": [
            "Le contenu, la conception du site, le nom Insidermark et son sceau appartiennent à Insidermark et ne peuvent être copiés sans autorisation."
          ]
        },
        {
          "h": "Responsabilité",
          "p": [
            "Le site est fourni en l'état. Dans la mesure permise par la loi, aucune responsabilité n'est acceptée pour les dommages indirects résultant de l'utilisation du contenu du site."
          ]
        },
        {
          "h": "Droit applicable",
          "p": [
            "Ces conditions sont régies par le droit de la République de Türkiye."
          ]
        }
      ]
    }
  },
};

const divisionsTr: Division[] = divisionsEn.map((d) => {
  const tr: Record<DivisionSlug, Partial<Division>> = {
    "automotive-heavy-duty": {
      name: "Otomotiv ve Ticari Araçlar",
      short: "Komple ticari araçlar, ağır vasıta yedek parça ve konsolidasyon deposu.",
      scope: "Otobüs, çekici ve ambulans gibi özel amaçlı ticari araçların anahtar teslim ihracatı, ağır vasıta yedek parçası ve karma parça siparişlerinin tek depoda konsolidasyonu. MAN, Mercedes-Benz ve TEMSA platformlarında güçlüyüz.",
      products: [
        { name: "Komple araç ihracatı", detail: "Otobüs, çekici, kamyon ve özel amaçlı araçlar; Afrika, Orta Doğu ve Orta Asya için anahtar teslim" },
        { name: "Ağır vasıta yedek parça", detail: "Fren sistemlerinden süspansiyona; motor, şanzıman, diferansiyel, elektrik, kaporta, şasi, dorse ve hava yayı grupları" },
        { name: "Parça konsolidasyonu", detail: "Farklı Avrupalı ve yerli markalardan gelen karma parça talepleri tek depoda toplanır, tek navlunla gönderilir" },
        { name: "Filo dönüşümü", detail: "Otobüs ve minibüs yenileme, yeniden tasarım ve filo teslimi" },
      ],
      capacity: [
        { label: "Dağıtım", value: "81 ilde 7.000+ teslimat noktası" },
        { label: "Depolama", value: "7 noktada 50.000 m2+" },
        { label: "Marka kapsamı", value: "Partnerin kataloğunda 60+ dünya markası" },
        { label: "İhracat ağı", value: "100+ ülke" },
      ],
      buyer: "MAN veya Mercedes-Benz ağır vasıta filosu işleten filo sahipleri, ithalatçılar ve toplu taşıma otoriteleri. Tipik sipariş: sevkiyat başına 50 ila 500 OEM kalemi.",
      markets: "Afrika, Orta Doğu ve Orta Asya. Tip onayı nedeniyle komple araçlar AB ve ABD'ye sunulmaz.",
      constraint: "AB ve ABD yedek parça pazarı parça başına marka ve sertifika kanıtı ister; oraya yalnızca belgelenmiş menşe ile teklif veriyoruz.",
    },
    "engineering-railway": {
      name: "İleri Mühendislik ve Demiryolu",
      short: "Demiryolu sistemleri, savunma sanayii bileşenleri, hidrolik silindir, döküm ve CNC.",
      scope: "Demiryolu sistemleri ve bileşenleri, savunma sanayii parçaları, ağır iş makineleri için hidrolik silindirler ve teknik çizime göre döküm, dövme ve CNC talaşlı imalat; Insidermark kalite doğrulamasıyla.",
      products: [
        { name: "Demiryolu sistemleri", detail: "Vagon fren sistemleri ve altyapı bileşenleri; TSI ve EN 15085 kaynak kapsamında" },
        { name: "Savunma sanayii bileşenleri", detail: "Savunma sanayii için yüksek güvenlikli parçalar" },
        { name: "Hidrolik silindirler", detail: "Endüstriyel ve ağır iş makineleri için standart ve özel silindirler" },
        { name: "Döküm, dövme ve CNC", detail: "Teknik çiziminize ve malzeme spesifikasyonunuza göre; 200 kg'a kadar dövme ve döküm, 30 tona kadar işleme" },
        { name: "Özel markalı üretim", detail: "Sizin çiziminiz, sizin markanız, partnerimizin hattı" },
      ],
      capacity: [
        { label: "Kapalı üretim alanı", value: "50.000 m2" },
        { label: "Dövme ve döküm", value: "200 kg'a kadar" },
        { label: "Talaşlı imalat", value: "30 t'a kadar" },
        { label: "Sertifikasyon", value: "TSI EN 15566, EN 15085, NDT Seviye 2" },
      ],
      buyer: "Tedarikçilerini denetleyen ve kalite yönetim belgeleri isteyen Avrupa, İspanya, ABD ve Meksika'daki OEM ve alt tedarikçiler. Tipik sipariş: teknik çizimden seri üretim.",
      markets: "OEM ve özel marka için Avrupa, İspanya, ABD ve Meksika; demiryolu bileşenleri için Kuzey Afrika ve Orta Doğu.",
      constraint: "Demiryolu bileşenleri yalnızca TSI ve EN 15085 kapsamıyla teklif edilir. Sertifika yoksa teklif yok.",
    },
    "agriculture-livestock": {
      name: "Tarım ve Hayvancılık Makineleri",
      short: "Modern çiftlikler için ağır toprak işleme, hasat, yem karma ve otomatik sağım.",
      scope: "Modern tarım ve endüstriyel büyükbaş hayvancılık için makineler: ağır toprak işleme ve hasat ekipmanları, yem karma makineleri ve otomatik sağım sistemleri; fabrika bandından proje sahanıza teslim.",
      products: [
        { name: "Ağır toprak işleme ve hasat", detail: "Traktör ataşmanları, pnömatik mibzerler, diskaro, rotatiller ve ağır hasat makineleri" },
        { name: "Yem karma makineleri", detail: "Büyükbaş çiftlikleri için endüstriyel yem karma makineleri" },
        { name: "Otomatik sağım sistemleri", detail: "Süt tesisleri için tam otomatik sağım istasyonları" },
        { name: "Hayvancılık tesisi teknolojisi", detail: "Büyük büyükbaş yatırımlarının makine altyapısı, projeye göre kapsamlandırılır" },
      ],
      capacity: [
        { label: "Toprak işleme partneri", value: "1980'den beri, Konya" },
        { label: "Referans", value: "Azerbaycan ulusal ihalesi" },
        { label: "Referans", value: "ABD'ye ilk Türk tarım makinesi ihracatı" },
        { label: "Teslimat", value: "Fabrika bandından proje sahanıza" },
      ],
      buyer: "Çiftlikler, kooperatifler, süt ve besi yatırımcıları, bakanlıklar ve kalkınma bankası destekli programlar. Tipik sipariş: bir makine filosu veya bir hayvancılık tesisinin ekipmanı.",
      markets: "ABD dahil dünya geneli.",
      constraint: "AB için CE ve makine direktifi uygunluğu gerekir.",
    },
    "industrial-plants": {
      name: "Endüstriyel Tesis Çözümleri",
      short: "Değirmen makineleri ve anahtar teslim fabrika kurulumu, makineden komple tesise.",
      scope: "Yüksek kapasiteli un, irmik ve yem değirmen makineleri, silo ve tahıl elleçleme sistemleri ve komple anahtar teslim tesis kurulumu: yeni fabrikalar, kapasite artırımı ve mekanik entegrasyon baştan sona yönetilir.",
      products: [
        { name: "Anahtar teslim un fabrikaları", detail: "Günde 50 ila 500 ton; kurulum, devreye alma ve operatör eğitimi dahil" },
        { name: "İrmik ve yem tesisleri", detail: "Durum hatları ve karma yem tesisleri" },
        { name: "Silo ve tahıl elleçleme", detail: "Çelik silolar, taşıma, kabul ve yükleme sistemleri" },
        { name: "Tesis kurulumu ve modernizasyon", detail: "Sıfırdan fabrika kurulumu, kapasite artışı, valsli değirmen yenileme, otomasyon ve mekanik entegrasyon" },
      ],
      capacity: [
        { label: "Referans", value: "Günde 150 ton, 10 valsli değirmen, Cezayir" },
        { label: "Teslim süresi", value: "35 gün üretim, 15 gün kurulum" },
        { label: "Kuruluş", value: "1994, Konya" },
        { label: "Servis", value: "Kurulum, eğitim, yedek parça planı" },
      ],
      buyer: "Yeni tesis veya kapasite artışı planlayan yatırımcılar, değirmenciler ve tarım-gıda grupları. Tipik sipariş: yedek parça planı ve finansman yapısıyla anahtar teslim tesis.",
      markets: "Talebe göre dünya geneli, en güçlü olduğumuz yer Afrika.",
      constraint: "Kurulum, devreye alma ve eğitim her zaman fiyata dahildir. Yedek parça planı olmadan teklif edilen tesis, tesis değildir.",
    },
    "custom-sourcing": {
      name: "Özel Proje ve Tedarik Masası",
      short: "Standart portföyümüzün dışındaki makine ve parçalar için proje masamız.",
      scope: "Portföyümüzde olmayan veya özel üretim gerektiren makine ve parçalar için Türkiye ağır sanayi ekosistemini tarar, doğru üreticiyi eşleştirir, maliyeti optimize eder ve üretimi sahada kendi kalite standartlarımızla yönetiriz.",
      products: [
        { name: "Üretici eşleştirme", detail: "Spesifikasyonunuza uygun Türk üretici, nitelikli tedarikçi havuzundan seçilir" },
        { name: "Maliyet optimizasyonu", detail: "Karşılaştırılabilir teklifler tek net teklifte toplanır" },
        { name: "Gözetimli üretim", detail: "Siparişten yüklemeye sahada takip" },
        { name: "Insidermark doğrulaması", detail: "Sevkiyat öncesi ürün doğrulaması; her sevkiyatla aynı QC raporu" },
      ],
      capacity: [
        { label: "Erişim", value: "Türkiye ağır sanayi ekosistemi" },
        { label: "Süreç", value: "Eşleştir, gözetle, doğrula" },
        { label: "Masa", value: "Yönetici ortağa doğrudan hat" },
      ],
      buyer: "Hiçbir kataloğun kapsamadığı belirli bir makine veya parçası olan alıcılar. Tipik sipariş: hedef adetle birlikte bir çizim veya spesifikasyon.",
      markets: "Talebe göre dünya geneli, en güçlü olduğumuz yer Afrika.",
      constraint: "Talebi yalnızca nitelikli bir üretici belirlenip doğrulanabiliyorsa kabul ederiz. Edemiyorsak ilk cevapta söyleriz.",
    },
  };
  return { ...d, ...tr[d.slug] } as Division;
});

const marketsTr: Market[] = marketsEn.map((m) => {
  const names: Record<string, string> = { algeria: "Cezayir", tunisia: "Tunus", morocco: "Fas", senegal: "Senegal", nigeria: "Nijerya", libya: "Libya", egypt: "Mısır", "ivory-coast": "Fildişi Sahili", ghana: "Gana", kenya: "Kenya", ethiopia: "Etiyopya", tanzania: "Tanzanya" };
  const regions: Record<string, string> = { "North Africa": "Kuzey Afrika", "West Africa": "Batı Afrika", "East Africa": "Doğu Afrika" };
  const detail: Record<string, Partial<Market>> = {
    algeria: { why: "Partner ağımızın en güçlü anahtar teslim değirmen referansı Cezayir'de duruyor; İzmir'den kısa deniz hattı transit süresini öngörülebilir tutuyor.", importRules: ["Birçok ürün grubu için sevkiyat öncesi uygunluk değerlendirme programları uygulanır; kapsam sık değişir, güncel rejim her siparişten önce teyit edilir.", "Ticari belgeler Fransızca; gümrük yazışmalarında Arapça kabul edilir.", "Yeni alıcılar: teyitli akreditif veya peşin ödeme."], logistics: { origin: "İzmir veya Mersin", destination: "Cezayir, Oran", transit: "5 ila 8 gün" }, reference: "10 valsli değirmenle günde 150 ton kapasiteli un fabrikası, 50 günde devreye alındı (35 gün üretim, 15 gün kurulum).", language: "Fransızca ve Arapça konuşan muhatap." },
    tunisia: { why: "Sahadaki bağlantılarımız ve İzmir'den kısa deniz hatları Tunus'u değirmen, tarım makinesi ve ağır vasıta parçaları için doğal bir giriş pazarı yapıyor.", importRules: ["İthalatta teknik kontrol tanımlı ürün listelerine uygulanır; sevkiyattan önce GTİP koduna göre doğrulanır.", "Fransızca dokümantasyon.", "Kur hareketlerine karşı kısa teklif geçerliliği (7 ila 15 gün)."], logistics: { origin: "İzmir", destination: "Radès (Tunus), Sfax", transit: "4 ila 7 gün" }, reference: "En yakın referans: sınırın hemen yanındaki Cezayir anahtar teslim değirmeni.", language: "Fransızca ve Arapça konuşan muhatap." },
    morocco: { why: "Tarım mekanizasyonu talebi yapısal ve Fas'ın SIAM fuarı yıllık saha takvimimizde.", importRules: ["Düzenlemeye tabi endüstriyel ürünlerde uygunluk kontrolü; kapsam sipariş başına teyit edilir.", "Fransızca dokümantasyon; tüketim malları için Arapça etiketleme kuralları.", "İlk işlemlerde akreditif olağandır."], logistics: { origin: "İzmir", destination: "Kazablanka, Tanger Med", transit: "8 ila 12 gün" }, reference: "Konya kümesinden toprak işleme makinesi referansları.", language: "Fransızca ve Arapça konuşan muhatap." },
    senegal: { why: "Kalkınma bankası destekli tarım ve gıda altyapı programları Senegal'i Batı Afrika giriş pazarımız yapıyor.", importRules: ["İthalat doğrulama programları sevkiyat öncesi denetim gerektirebilir; sipariş başına teyit edilir.", "Baştan sona Fransızca dokümantasyon.", "Projeyi çoğunlukla finansman belirler: Eximbank alıcı kredisi ve ihracat kredi sigortası teklife dahil edilir."], logistics: { origin: "Ambarlı veya Kocaeli", destination: "Dakar", transit: "18 ila 25 gün" }, reference: "Kuzey Afrika'da kanıtlanmış anahtar teslim tesis modeli; kurulum ve eğitim dahil.", language: "Fransızca konuşan muhatap." },
    nigeria: { why: "Batı Afrika'nın en büyük ağır vasıta parkı ve kapasitesini genişletmeye devam eden bir değirmencilik sektörü.", importRules: ["Düzenlemeye tabi ürünler için SONCAP uygunluk değerlendirmesi zorunludur; sertifikasız konteyner limanda bekler ve depolama maliyeti doğurur.", "İngilizce dokümantasyon.", "Yeni alıcılar: teyitli akreditif veya peşin ödeme; döviz transfer gecikmeleri gerçek bir risktir, bu yüzden teklif geçerliliği kısa tutulur."], logistics: { origin: "Ambarlı veya Kocaeli", destination: "Lagos (Apapa, Tin Can)", transit: "20 ila 28 gün" }, reference: "Otomotiv partnerlerimizin MAN ve Mercedes-Benz yedek parça programları.", language: "İngilizce konuşan muhatap." },
    libya: { why: "Mersin'e yakın ağır vasıta parkı ve tahıl işleme ihtiyaçları.", importRules: ["Rejim sipariş başına teyit edilir."], logistics: { origin: "Mersin", destination: "Trablus, Misrata", transit: "4 ila 7 gün" }, reference: "Program hazırlık aşamasında.", language: "Arapça konuşan muhatap." },
    egypt: { why: "Değirmencilik kapasitesi ve demiryolu bileşeni talebi.", importRules: ["Ön kayıt ve uygunluk gereklilikleri uygulanır; sipariş başına teyit edilir."], logistics: { origin: "Mersin", destination: "İskenderiye, Dimyat", transit: "3 ila 6 gün" }, reference: "Program hazırlık aşamasında.", language: "Arapça ve İngilizce konuşan muhatap." },
    "ivory-coast": { why: "Tarım-gıda yatırım koridoru.", importRules: ["İthalat doğrulama programı; sipariş başına teyit edilir."], logistics: { origin: "Ambarlı", destination: "Abidjan", transit: "20 ila 26 gün" }, reference: "Program hazırlık aşamasında.", language: "Fransızca konuşan muhatap." },
    ghana: { why: "Mekanizasyon programları ve filo yenileme.", importRules: ["Uygunluk değerlendirme programı; sipariş başına teyit edilir."], logistics: { origin: "Ambarlı", destination: "Tema", transit: "20 ila 26 gün" }, reference: "Program hazırlık aşamasında.", language: "İngilizce konuşan muhatap." },
    kenya: { why: "Değirmencilik ve tarım mekanizasyonu için Doğu Afrika merkezi.", importRules: ["Düzenlemeye tabi mallar için PVoC ihracat öncesi doğrulama zorunludur."], logistics: { origin: "Mersin", destination: "Mombasa", transit: "18 ila 24 gün" }, reference: "Program hazırlık aşamasında.", language: "İngilizce konuşan muhatap." },
    ethiopia: { why: "Büyük ölçekli tarım ve gıda işleme programları.", importRules: ["Uygunluk ve döviz tahsisi kuralları; sipariş başına teyit edilir."], logistics: { origin: "Mersin", destination: "Cibuti, devamında demiryolu", transit: "18 ila 24 gün artı kara taşıma" }, reference: "Program hazırlık aşamasında.", language: "İngilizce konuşan muhatap." },
    tanzania: { why: "Tahıl işleme ve tarım mekanizasyonu talebi.", importRules: ["PVoC sevkiyat öncesi doğrulama uygulanır."], logistics: { origin: "Mersin", destination: "Darüsselam", transit: "20 ila 26 gün" }, reference: "Program hazırlık aşamasında.", language: "İngilizce konuşan muhatap." },
  };
  return { ...m, name: names[m.slug] ?? m.name, region: regions[m.region] ?? m.region, ...detail[m.slug] } as Market;
});

const tr: Dictionary = {
  ...en,
  langName: "Türkçe",
  nav: { divisions: "Sektörler", advantage: "Tedarikin Ötesi", how: "Nasıl çalışıyoruz", markets: "Pazarlar", network: "Ağ", about: "Hakkımızda", contact: "İletişim", rfq: "Teklif isteyin", whatsapp: "WhatsApp", menu: "Menü", close: "Kapat" },
  journey: [
    { id: "origin", label: "Köken", title: "Türkiye'deki entegre tedarik ve operasyon merkeziniz.", body: "Yetkili üretici partnerliklerimiz ve talebe özel tedarik ağımızla ağır sanayi ihtiyaçlarınızı tek merkezden karşılıyoruz. Siparişlerinizi üretim sırasında sahada takip ediyor, sevkiyat öncesi ürünleri doğruluyor ve çoklu marka konsolidasyonunuzu baştan sona organize ediyoruz.", tags: ["Konya", "Ankara", "İstanbul"] },
    { id: "divisions", label: "Sektörler", title: "Beş sektör, tek muhatap.", body: "Ticari araçlar, mühendislik ve demiryolu, tarım ve hayvancılık makineleri, endüstriyel tesisler ve özel tedarik masası. Sorumlu tek bir kişiyle çalışırsınız.", tags: ["Özbaşak"] },
    { id: "inspection", label: "Denetim", title: "Yüklenmeden önce sahada doğrulanır.", body: "Siparişi üretim bandında takip eder, konteyner kapanmadan önce malı doğrularız. Her sevkiyat Insidermark mührünü taşıyan bir kalite kontrol raporuyla çıkar.", tags: ["Mapar", "Hıdırusta"] },
    { id: "consolidation", label: "Konsolidasyon", title: "Tek konteyner. Tek fatura. Tek sevkiyat.", body: "Farklı üreticilerin ürünleri, tek set ihracat belgesiyle tek bir karma konteynerde yola çıkar.", tags: ["FMC"] },
    { id: "world", label: "Dünya", title: "Türkiye'den dünyaya tedarik ortağınız.", body: "Afrika, Orta Doğu, Orta Asya, Avrupa ve Amerika'da aktif. Türkçe, İngilizce ve Fransızca destek, 48 saat içinde teklif." },
  ],
  hero: { secondary: "Nasıl çalışıyoruz", waText: "Merhaba Insidermark, teklif almak istiyorum.", slogan: "Türkiye'den dünyaya tedarik ortağınız." },
  partners: {
    title: "Partnerlerimiz",
    intro: "Her sevkiyatın arkasındaki üreticiler. Her partner, beş sektörümüzden birini temsil eder.",
    note: "İsimler ve markalar ilgili sahiplerine aittir.",
    items: [
      { name: "Mapar", logoKey: "mapar", sector: "Otomotiv ve Ticari Araçlar", body: "MAN, TEMSA, FUSO Canter ve ZF için yetkili bayi ve servis. Komple otobüs, kamyon ve özel amaçlı araç ihracatı, otobüs ve minibüs dönüşümü.", meta: "1980'den beri · Ankara, Bursa, İzmir", division: "automotive-heavy-duty" },
      { name: "Hıdırusta", logoKey: "hidirusta", sector: "Ağır vasıta yedek parça", body: "81 ilde 7.000'den fazla teslimat noktasında MAN ve Mercedes-Benz parçalarının toptan dağıtımı, 7 noktada 50.000 m2+ depo ve karma parça konsolidasyon noktamız.", meta: "1978'den beri · İstanbul", division: "automotive-heavy-duty" },
      { name: "FMC", logoKey: "fmc", sector: "İleri Mühendislik ve Demiryolu", body: "TSI EN 15566, EN 15085 ve NDT Seviye 2 ile dövme, döküm ve talaşlı imalat. 200 kg'a kadar dövme parça, 30 t'a kadar işleme, 45 ülkeye ihracat.", meta: "Yaklaşık 50 yıl · Konya", division: "engineering-railway" },
      { name: "Özbaşak", logoKey: "ozbasak", sector: "Endüstriyel Tesis Çözümleri", body: "Anahtar teslim un ve irmik fabrikaları, silolar, otomasyon ve satış sonrası. Cezayir'de günde 150 ton kapasiteli değirmen 50 günde devreye alındı.", meta: "1994'ten beri · Konya", division: "industrial-plants" },
    ],
  },
  engines: {
    title: "İki motor, tek muhatap.",
    intro: "Tedarik gücümüz iki kanada dayanır. Birini ya da ikisini aynı konteynerde kullanın.",
    items: [
      { tag: "Kanat 1", name: "Yetkili partnerlikler", body: "Ağımızdaki köklü Türk üreticilerin dış operasyon masası olarak hareket ederiz. Sektör liderlerinin doğrudan gücü, tek bir adresten." },
      { tag: "Kanat 2", name: "Talebe özel tedarik", body: "Kataloğa sığmayan projeler için bağımsız proje masamız sizi Türkiye ağır sanayisindeki doğru üreticiye bağlar. Kataloğa hapsolmayın: ihtiyacı söyleyin." },
    ],
  },
  advantage: {
    title: "Tedarikin Ötesi",
    subtitle: "Geleneksel tedarikin bittiği yer: alıcılar neden tek başına fabrikaya gitmek yerine Insidermark ile çalışıyor.",
    intro: "Uluslararası ağır sanayi ticaretinde en büyük maliyet ürünün kendisi değildir. Yönetilemeyen operasyonlar, üretim körlüğü ve parçalanmış lojistiktir. Insidermark masaya oturduğunda yalnızca istediğiniz ürünü bulmaz; tedarik zincirindeki gizli maliyetleri ve operasyonel riskleri ortadan kaldırır.",
    blocks: [
      { n: "01", label: "Fiyat konumu", myth: "Doğrudan fabrika yanılgısı", mythBody: "Yabancı alıcılar büyük bir üreticiye doğrudan ulaşınca en düşük fiyatı alacaklarını sanır. Oysa büyük fabrikalar tekil ve düzensiz alıcılara genellikle standart ihracat liste fiyatını verir.", truth: "Partner düzeyinde erişim", truthBody: "Üretici partnerlerimizle düzenli ve daha yüksek hacimli bir muhatap olarak çalışırız. Anlaşmalarımız ve konsolide satın alma gücümüzle, tek seferlik bir alıcının genellikle alamadığı partner düzeyi şartları ve üretim önceliğini size getirmeyi hedefleriz." },
      { n: "02", label: "Süreç", myth: "Kurumsal bürokrasi ve parçalı lojistik", mythBody: "Büyük üreticiler yavaş hareket eder. Özel spesifikasyonlar takılır, küçük karma siparişler reddedilir ve farklı markalardan alım yapınca her biri için ayrı navlun ve ayrı gümrük ödersiniz.", truth: "Konsolidasyon gücü", truthBody: "Kapıda bekleyen bir müşteri değil, sürecin içindeki operasyon masasıyız. Anlaşmalarımızla özel üretim taleplerini hızlandırır, farklı markaların karma siparişlerini tek lojistik noktada, tek konteynerde birleştiririz; beş ayrı navlun ve beş ayrı gümrük ödemezsiniz." },
      { n: "03", label: "Risk", myth: "Kalite körlüğü", mythBody: "Para gittikten sonra uçuş kördür. Ürün toleransında üretilecek mi, eksiksiz yüklenecek mi? Sürprizler ancak mal gümrüğünüze indiğinde ortaya çıkar ve geri dönüş yoktur.", truth: "Saha gözetimi ve sözleşmesel teminat", truthBody: "Operasyonu masa başından veya e-postayla yönetmeyiz. Siparişiniz bandındayken sahaya iner, üretimi takip eder ve konteyner mühürlenmeden önce Insidermark QC doğrulamasını uygularız. Ticaretiniz yazılı anlaşmalara ve sahadaki gözetimimize dayanır." },
    ],
    ctaLine: "Türkiye ağır sanayisine standart bir müşteri olarak değil, öncelikli bir partner olarak bağlanın.",
    ctaRfq: "B2B teklif talebi başlatın",
    ctaDesk: "Yönetim Masası",
  },
  proof: [
    { value: "5", label: "sektör, tek muhatap" },
    { value: "5", label: "üretim partneri, Konya'dan İstanbul'a" },
    { value: "1975", label: "en eski partnerin kuruluş yılı" },
    { value: "100+", label: "partnerlerimizin zaten ihracat yaptığı ülke" },
  ],
  divisionsSection: { title: "Beş ana sektör, tek muhatap.", intro: "Türkiye'de beş ayrı şirkete yazmak yerine tek bir muhatapla çalışırsınız. Kapsamı, kapasiteyi ve uygun olduğu pazarları görmek için bir sektörü açın.", open: "Sektörü aç" },
  layers: {
    title: "Üç sütun, birlikte sunulur.",
    intro: "Tek başına erişim bizi bir komisyoncu yapardı. İkinci ve üçüncü sütun, yerine koyması zor olan kısımdır.",
    items: [
      { name: "Partner düzeyi fiyat", body: "Fiyatımız, üreticiyle sahip olduğumuz koşulları ve bunun arkasındaki operasyonu yansıtır. Her teklifte fiyatın neleri kapsadığı yazılıdır, sürpriz maliyet çıkmaz." },
      { name: "Sahada doğrulama", body: "Sipariş bandındayken sahaya iner, üretimi takip eder ve konteyner mühürlenmeden önce malı doğrularız. Insidermark mührünü taşıyan QC raporu, konteynerden önce size ulaşır." },
      { name: "Akıllı konsolidasyon", body: "Farklı üreticilerden gelen karma talepler tek lojistik noktada toplanır ve tek konteynerde gönderilir. Her marka için ayrı navlun ve gümrükten kurtulursunuz." },
    ],
  },
  consolidation: {
    eyebrow: "Konsolidasyon koridoru",
    title: "Tek konteyner. Tek fatura. Tek sevkiyat.",
    body: "Partnerlerimizin birçoğu aynı şehirde. Konya'daki tek toplama noktası birden fazla sektörü tek kamyona yükler; otomotiv hattı Ankara ve İstanbul'dan katılır.",
    nodes: { konya: "Konya", ankara: "Ankara", istanbul: "İstanbul", cluster: "Konya kümesi" },
    ports: { mersin: "Mersin", izmir: "İzmir", ambarli: "Ambarlı" },
    portsNote: { mersin: "Doğu Kuzey Afrika, Mısır, Libya, Körfez", izmir: "Cezayir, Tunus, Fas", ambarli: "Batı ve Doğu Afrika, okyanus" },
    africa: "Dünya",
    manifestTitle: "Konsolide sevkiyat manifestosu",
    foot: [
      { label: "Taşıma", value: "Deniz yolu, FCL / LCL" },
      { label: "Toplama", value: "Konya, Ankara, İstanbul" },
      { label: "Limanlar", value: "Mersin, İzmir, Ambarlı" },
      { label: "Belgeler", value: "Tek set, kayıtlı ihracatçı" },
    ],
    manifest: [
      { label: "Gönderici", value: "Insidermark, kayıtlı ihracatçı" },
      { label: "Çıkış", value: "Konya / Ankara / İstanbul" },
      { label: "Kargo", value: "Karma endüstriyel, tek çeki listesi" },
      { label: "Belgeler", value: "Proforma, QC raporu, menşe şahadetnamesi" },
    ],
  },
  marketsSection: {
    title: "Türkiye'den dünyaya.",
    body: "Değirmen tesisleri, tarım ve hayvancılık makineleri, ticari araçlar ve ağır vasıta parçaları, büyüyen pazarların en çok satın aldığı ürünlerdir. Türkiye, Avrupa fiyatları ile Uzak Doğu satış sonrası boşlukları arasında duruyor. Ağımız Kuzey ve Batı Afrika'da en güçlü; talebe göre dünya genelinde çalışıyoruz.",
    priorities: [
      { name: "Kuzey Afrika", countries: "Tunus, Cezayir, Fas, Libya, Mısır", slug: "algeria" },
      { name: "Sahra altı Afrika", countries: "Senegal, Fildişi Sahili, Gana, Nijerya, Kenya, Etiyopya, Tanzanya", slug: "nigeria" },
      { name: "Orta Doğu ve Körfez", countries: "Irak, Ürdün, Suudi Arabistan, BAE", slug: "algeria" },
      { name: "Orta Asya ve Kafkasya", countries: "Azerbaycan, Özbekistan, Kazakistan, Gürcistan", slug: "algeria" },
      { name: "Avrupa ve Amerika", countries: "İspanya, Meksika, ABD", slug: "algeria" },
    ],
    open: "Pazarı aç",
  },
  process: {
    title: "Nasıl çalışıyoruz",
    intro: "Sekiz adım, tek muhatap, 48 saatlik teklif hedefi.",
    steps: [
      { title: "Talep", body: "Talebiniz form, Excel listesi veya tek sayfalık PDF ile gelir. Eksik teknik veri süreyi durdurur." },
      { title: "Fabrika eşleştirme", body: "Talep en uygun partnere yönlendirilir. Ağ dışındaki kalemler yalnızca tedarikçi kalite kontrolünden sonra teklif edilir." },
      { title: "Teklif", body: "Fiyat, teslim süresi, Incoterms, ödeme koşulları ve QC kapsamı tek belgede. Hedef: 48 saat." },
      { title: "Uygunluk", body: "Ülkenizin ithalat gereklilikleri okunur; denetimler ve sertifikalar fiyata dahil edilir." },
      { title: "Sözleşme", body: "Ödeme koşulları yazılı olarak sabitlenir. Yeni alıcılar: peşin veya teyitli akreditif." },
      { title: "Üretim takibi", body: "Hat takip edilir ve düzenli durum alırsınız. Teslim süresi sapması ortaya çıktığı anda bildirilir." },
      { title: "Kalite kontrol", body: "Insidermark mühürlü sevkiyat öncesi QC raporu. Karma siparişler Konya toplama noktasında konsolide edilir." },
      { title: "Sevkiyat", body: "Gümrük ve navlun organize edilir. Kurulum, eğitim ve yedek parça planı teslimattan sonra başlar." },
    ],
    note: "Kötü haber önce gelir. Gecikme, kalite sorunu veya fiyat değişikliği ortaya çıktığı anda bildirilir.",
  },
  references: {
    eyebrow: "Ağ referansları",
    title: "Ağın şimdiye kadar inşa ettikleri.",
    items: [
      { title: "50 günde günde 150 ton un fabrikası", where: "Cezayir", body: "On valsli değirmen, 35 gün üretim ve 15 gün kurulum. Değirmen partnerimiz tarafından devreye alındı; bugün teklif verdiğimiz her Kuzey Afrika tesisi için en yakın referans.", image: "/assets/refs/algeria-mill.jpg", facts: ["Günde 150 ton", "10 valsli değirmen", "50 gün"] },
      { title: "Ulusal ihale, toprak işleme filosu", where: "Azerbaycan", body: "Tarım partnerimiz Azerbaycan ulusal ihalesini kazandı ve ABD'ye ilk Türk tarım makinesi ihracatını gerçekleştirdi.", image: "/assets/refs/azerbaijan-tillage.jpg", facts: ["Ulusal ihale", "ABD ihracatı"] },
    ],
    disclaimer: "Referanslar üretim partnerlerimize aittir ve izinle gösterilir. Insidermark'ın kendi ilk sevkiyatları tamamlandıkça vaka çalışması olarak belgelenir.",
  },
  closing: { title: "Listeyi gönderin. Gerisini biz hallederiz.", body: "48 saat içinde, sizin dilinizde teklif." },
  footer: {
    address: "Konya · İstanbul, Türkiye",
    columns: [
      { title: "Sektörler", links: [
        { label: "Otomotiv ve Ticari Araçlar", to: "/divisions/automotive-heavy-duty" },
        { label: "İleri Mühendislik ve Demiryolu", to: "/divisions/engineering-railway" },
        { label: "Tarım ve Hayvancılık Makineleri", to: "/divisions/agriculture-livestock" },
        { label: "Endüstriyel Tesis Çözümleri", to: "/divisions/industrial-plants" },
        { label: "Özel Proje ve Tedarik Masası", to: "/divisions/custom-sourcing" },
      ] },
      { title: "Şirket", links: [
        { label: "Tedarikin Ötesi", to: "/beyond-sourcing" },
        { label: "SSS", to: "/faq" },
        { label: "Nasıl çalışıyoruz", to: "/how-we-work" },
        { label: "Üretim ağı", to: "/manufacturing-network" },
        { label: "Pazarlar", to: "/markets" },
        { label: "Hakkımızda", to: "/about" },
        { label: "İletişim", to: "/contact" },
      ] },
      { title: "Teklif isteyin", links: [
        { label: "Yedek parça (Excel yükleme)", to: "/rfq/spare-parts" },
        { label: "Özel imalat (çizim)", to: "/rfq/custom-manufacturing" },
        { label: "Anahtar teslim proje", to: "/rfq/turnkey-project" },
      ] },
    ],
    legal: "Insidermark | Global Sourcing Partner.",
    privacy: "Gizlilik", terms: "Kullanım koşulları",
    languages: "Dil",
  },
  divisions: divisionsTr,
  divisionPage: { products: "Ürün ve hizmetler", capacity: "Doğrulanmış kapasite", buyer: "Tipik alıcı ve sipariş", markets: "Uygun pazarlar", constraint: "Dürüstçe söylediklerimiz", rfq: "Bu sektör için teklif isteyin", back: "Tüm sektörler", catalog: "Katalog", catalogNote: "Kataloglar, teklif formu üzerinden talep edildiğinde e-postayla gönderilir." },
  markets: marketsTr,
  marketPage: { activeDivisions: "Bu pazarda aktif sektörler", importRules: "İthalat gereklilikleri, özet", importNote: "Rejimler sık değişir. Güncel kapsamı her siparişten önce teyit eder ve denetimi sizin adınıza yönetiriz.", logistics: "Lojistik", origin: "Yükleme limanı", destination: "Boşaltma limanı", transit: "Tahmini transit", reference: "En yakın referans", language: "Sizin diliniz", rfq: "Bu pazar için teklif isteyin", all: "Tüm pazarlar", phase2: "Program ikinci aşamada açılıyor" },
  how: {
    title: "Nasıl çalışıyoruz",
    intro: "Birlikte sunulan üç katman, sekiz adım ve esnetmediğimiz kurallar.",
    representation: { title: "Yetkili iş ortaklığı", body: [
      "Köklü Türk üreticilerin yetkili iş ortağı olarak çalışırız. Yurt dışından gelen talepler üreticiler tarafından bize yönlendirilir; teklifi, üretim takibini, doğrulamayı ve sevkiyatı tek muhatabınız olarak yönetiriz.",
      "Hangi sektörden alım yaparsanız yapın tek bir muhatapla çalışırsınız. İçerideki karmaşıklığı yönetmek bizim işimiz, görmek sizin değil.",
    ] },
    quality: { title: "Kalite kontrol", body: [
      "Sipariş onayından yüklemeye kadar üretim hattı takip edilir. Konteyner mühürlenmeden önce size Insidermark mühürlü bir QC raporu verilir: fotoğraflar, ölçülen kalemler, ambalaj kontrolü, işaretleme kontrolü.",
      "Birçok ithalat pazarı bağımsız sevkiyat öncesi denetim ister. Zaten yapılması gerekiyorsa aynı adımın parçası olarak sizin için yönetiriz.",
    ], report: [
      { label: "Rapor", value: "QC-2026-0000 (örnek düzen)" },
      { label: "Sevkiyat", value: "Karma konteyner, 3 üretici" },
      { label: "Kontrol edilen", value: "Miktar, işaretleme, ambalaj, ölçü" },
      { label: "Fotoğraflar", value: "Her kalem için, ambalaj öncesi ve sonrası" },
      { label: "Sonuç", value: "Yükleme için serbest" },
    ] },
    consolidation: { title: "Konsolidasyon ve lojistik", body: [
      "Beş fabrikadan doğrudan almak beş sözleşme, beş navlun, beş gümrük işlemi ve takip edilecek beş teslim süresi demektir. Bunu tek sevkiyata indiriyoruz.",
      "Şehirdeki sanayi partnerlerimiz için Konya toplama noktası; otomotiv kalemleri Ankara-İstanbul hattında toplanır. Limanlar: Doğu Kuzey Afrika, Mısır, Libya ve Körfez için Mersin; Cezayir, Tunus ve Fas için İzmir; Batı ve Doğu Afrika için Ambarlı ve Kocaeli.",
    ] },
    incoterms: { title: "Incoterms, sade dille", intro: "Kim neyi öder ve risk nerede el değiştirir.", terms: [
      { code: "EXW", name: "Fabrika teslim", body: "Malı fabrika kapısından siz alırsınız. Kapıdan sonrası, ihracat gümrüğü dahil, sizindir." },
      { code: "FOB", name: "Gemide teslim", body: "Malı Türk limanında gemiye, ihracat gümrüklenmiş olarak biz teslim ederiz. Deniz navlunu ve sigorta sizindir." },
      { code: "CIF", name: "Mal bedeli, sigorta ve navlun", body: "Navlun ve sigortayı limanınıza kadar biz öderiz. İthalat gümrüğü ve kara taşıma sizindir." },
    ] },
    payment: { title: "Ödeme", body: [
      "Yeni alıcılar: peşin ödeme veya teyitli akreditif. Vadeli koşullar yalnızca ticari geçmiş oluştuktan sonra, kredi sigortasıyla değerlendirilir.",
      "Navlun ve kur oynadığı için teklif geçerliliği kısa, 7 ila 15 gün tutulur. Ödeme koşulları yazılı olarak sabitlenmeden üretim başlamaz.",
      "Anahtar teslim projeler için mümkün olduğunda Türk Eximbank alıcı kredisi ve ihracat kredi sigortası yapılandırırız. Birçok pazarda projeyi çoğu zaman fiyattan çok finansman kapatır.",
    ] },
    faq: "Hep sorulan bir soru",
    bypass: { q: "Neden doğrudan fabrikadan almıyorsunuz?", a: "Onlara ulaşabilirsiniz ve birçok alıcı bunu yapıyor. Ancak tekil ve düzensiz bir alıcıya genellikle standart ihracat fiyatı verilir ve beş sözleşme, beş navlun ve beş teslim süresiyle sıraya girer. Biz partner düzeyi şartları getirmeyi, işi tek sevkiyata indirmeyi hedefliyoruz; sevkiyat öncesi QC raporu da sahadaki gözünüzdür." },
  },
  network: {
    title: "Üretim ağı",
    intro: "Beş sektör, beş üretim partneri. Liste bilinçli olarak kısa: derin bir ilişki, uzun bir listeden daha kolay savunulur.",
    partners: [
      { area: "Ticari araç yedek parça, İstanbul", since: "1978'den beri", capacity: "81 ilde 7.000+ teslimat noktası, 7 noktada 50.000 m2+ depo, 60+ marka", certs: "MAN ve Mercedes-Benz için toptan dağıtım", division: "automotive-heavy-duty" },
      { area: "Ticari araçlar, Ankara, Bursa, İzmir", since: "1980'den beri", capacity: "MAN, TEMSA, FUSO Canter ve ZF için yetkili bayi ve servis; otobüs ve minibüs dönüşümü", certs: "Yetkili bayi ağı", division: "automotive-heavy-duty" },
      { area: "Dövme, döküm ve talaşlı imalat, Konya", since: "Yaklaşık 50 yıl", capacity: "50.000 m2 kapalı alan, 200 kg'a kadar dövme, 30 t'a kadar işleme, yaklaşık 1.000 çalışan, 45 ülkeye ihracat", certs: "TSI EN 15566, EN 15085, NDT Seviye 2, test laboratuvarı", division: "engineering-railway" },
      { area: "Değirmen makineleri, Konya", since: "1994'ten beri", capacity: "Anahtar teslim un ve irmik tesisleri, silolar, otomasyon, satış sonrası", certs: "Referans: Cezayir'de günde 150 ton tesis, 50 gün", division: "industrial-plants" },
      { area: "Tarım makineleri, Konya", since: "1980'den beri", capacity: "Rotatiller, dikey kültivatörler, parçalayıcılar, sıra arası çapalar", certs: "Azerbaycan ulusal ihalesi; ABD'ye ilk Türk tarım makinesi ihracatı", division: "agriculture-livestock" },
    ],
    corridor: "Konya kümesi: tek şehirde birkaç partner, tek toplama noktası, tek kamyon. Otomotiv için Ankara-İstanbul hattı. Limanlar: Mersin, İzmir, Ambarlı ve Kocaeli.",
    rule: "Üreticilerin web sitelerine bağlantı vermeyiz. Kapasite, tesisler ve sertifikalar gösterilir; ilişkiyi Insidermark yönetir.",
  },
  about: {
    title: "Insidermark hakkında",
    vision: { title: "Vizyon", body: "Dünyadaki alıcılar için Türkiye'den sanayi ve tarım tedarikinde ilk adres olmak; Türk üretimini alıcının kendi ülkesindeki bir satın alma ofisi kadar ulaşılabilir kılmak." },
    mission: { title: "Misyon", body: "Farklı üreticilerin kapasitesini tek bir satın alma arayüzünde birleştirmek ve her sevkiyatta denetlenmiş kalite, tek muhatap ve öngörülebilir teslim süresiyle alıcının tedarik riskini taşımak." },
    values: { title: "Değerler", items: [
      { name: "Doğrulanabilir", body: "Söylediğimiz her şey bir belgeyle kanıtlanabilir: sertifika, denetim raporu, referans proje. Belgesiz iddia kullanmayız." },
      { name: "Tek muhatap", body: "Hangi sektörden alım yaparsanız yapın, tek bir kişi sorumludur." },
      { name: "Net teklif", body: "Fiyat; ürüne, hacme ve sevkiyat kapsamına göre değişir. Her teklifte fiyatın neleri kapsadığı (ürün, kalite kontrol, konsolidasyon, lojistik) açıkça yazılır." },
      { name: "Kötü haber önce", body: "Gecikme, kalite sorunu veya fiyat değişikliği ortaya çıktığı anda bildirilir." },
      { name: "Uzun vade", body: "Tek seferlik marjdan çok tekrar eden siparişler. Başarı birinci değil, üçüncü siparişte ölçülür." },
    ] },
    notWeAre: { title: "Ne olmadığımız", items: ["Komisyoncu değiliz: tek bir işlemde değil, üretim takibinden teslimata kadar tüm süreçte üretici ile alıcının arasında durur ve sorumluluk alırız.", "Fiyat aracısı değiliz: fiyatta değil, tedarik güvenliği ve operasyonda rekabet ederiz.", "Tek seferlik tüccar değiliz: amacımız markanın pazarınızdaki kalıcı adresi olmaktır."] },
    whyTurkiye: { title: "Neden Türkiye", body: [
      "Birçok pazardaki alıcılar Avrupa fiyatlarını ödeyemez ve Uzak Doğu satış sonrası boşluklarına güvenmez. Türkiye tam bu aralıkta duruyor: Avrupa standartları, kısa deniz hatları ve onlarca yıldır ihracat yapan üreticiler.",
      "Konya tek başına dövme, değirmencilik ve tarım makinesi üretimini tek şehirde barındırır. Karma konteyneri mümkün kılan bu coğrafyadır.",
    ] },
    team: { title: "Yönetim Masası", body: "Her talep sorumlu tek bir muhatapla ve gerektiğinde doğrudan yönetici ortakla ilerler. Proje düzeyi talepler için Yönetim Masası'na yazın." },
  },
  contact: {
    title: "İletişim",
    body: "Türkçe, İngilizce veya Fransızca yazın. Teklifler 48 saat içinde yanıtlanır.",
    email: "E-posta", whatsapp: "WhatsApp", offices: "Ofisler", hours: "Çalışma saatleri", languagesSpoken: "Diller",
    spokenList: "Türkçe, İngilizce, Fransızca. Talep üzerine Arapça.",
    hoursValue: "Pzt-Cum, 09:00 - 18:00 (UTC+3)",
    desk: { title: "Yönetim Masası", body: "Direct Desk: Yönetici Ortak. Karar vericiye ihtiyaç duyan proje düzeyi talepler için bu hat, bir destek kuyruğuna değil doğrudan yönetici ortağa bağlanır.", cta: "Yönetim Masasını aç" },
  },
  rfq: {
    title: "Teklif isteyin",
    intro: "Üç akış. İhtiyacınıza uyanı seçin; form yalnızca o ihtiyacın gerektirdiğini sorar.",
    flows: [
      { flow: "spare-parts", title: "Yedek parça", body: "Uzun OEM kod listeleri. Excel şablonunu indirin, doldurun, yükleyin. Liste hiçbir zaman tarayıcıya elle yazılmaz.", time: "48 saat" },
      { flow: "custom-manufacturing", title: "Özel imalat ve tedarik", body: "Dövme, döküm, CNC talaşlı imalat, özel marka ve portföyümüz dışındaki her makine veya parça. Çizim veya spesifikasyonu PDF, DWG ya da STEP olarak gizlilik altında yükleyin.", time: "3 ila 5 gün" },
      { flow: "turnkey-project", title: "Anahtar teslim proje", body: "Değirmenler, silolar, hayvancılık tesisleri, makine filoları. Teknik bir danışma görüşmesiyle biten daha uzun bir brif.", time: "5 gün içinde danışma" },
    ],
    common: {
      company: "Şirket", contact: "İletişim kişisi", email: "E-posta", phone: "Telefon veya WhatsApp", country: "Ülke", language: "Tercih edilen dil",
      port: "Varış limanı", incoterms: "Incoterms", consolidation: "Konsolidasyon", consolidationHint: "Birden fazla üreticinin kalemlerini tek konteynerde birleştir",
      notes: "Notlar", file: "Dosya", fileHint: "Excel, CSV, PDF, DWG veya STEP, en fazla 25 MB", submit: "Talebi gönder", submitting: "Gönderiliyor",
      privacy: "Yüklenen dosya ve çizimler yalnızca teklifinizi hazırlamak için kullanılır ve talebiniz için seçilen üretim partneri dışında paylaşılmaz.",
      required: "Zorunlu", error: "Talep gönderilemedi. Lütfen tekrar deneyin veya doğrudan bize yazın.",
      consent: "Aydınlatma metnini okudum; verilerimin teklifimi hazırlamak amacıyla işlenmesini kabul ediyorum.", consentLink: "aydınlatma metni", whatsappContinue: "WhatsApp ile devam et", consentError: "Talebi göndermek için aydınlatma metnini kabul etmelisiniz.",
    },
    spare: { brand: "Araç markası ve modeli", brandHint: "MAN TGS, Mercedes-Benz Actros, TEMSA ...", template: "Excel şablonunu indirin", templateHint: "Sütunlar: OEM kodu, tanım, marka, adet, hedef fiyat, araç modeli veya şasi.", upload: "Kalem listenizi yükleyin" },
    custom: { sector: "Sektör", material: "Malzeme spesifikasyonu", quantity: "Adet", series: "Sipariş türü", seriesOptions: ["Tek seferlik", "Seri"], upload: "Teknik çizimi yükleyin", nda: "Çizimler gizlilik altında işlenir. Yüklemeden önce talep üzerine imzalı gizlilik sözleşmesi sunulur." },
    turnkey: {
      plant: "Proje türü", plantOptions: ["Un fabrikası", "İrmik veya yem tesisi", "Silo ve tahıl elleçleme", "Hayvancılık tesisi (sağım, yem karma)", "Makine filosu", "Diğer"],
      capacity: "Kapasite ihtiyacı", capacityHint: "tesisler için ton/gün, hayvancılık tesisleri için baş sayısı", site: "Saha durumu", siteOptions: ["Yalnızca arsa", "Mevcut bina", "Modernize edilecek mevcut tesis", "Karar verilmedi"],
      budget: "Bütçe aralığı", budgetOptions: ["500 bin USD altı", "500 bin - 2 milyon USD", "2 - 10 milyon USD", "10 milyon USD üzeri", "Belirlenmedi"],
      timeline: "Zamanlama", timelineOptions: ["6 ay içinde", "6 - 12 ay", "12 - 24 ay", "Keşif aşamasında"],
      financing: "Finansman", financingOptions: ["Öz kaynak", "Banka finansmanı", "Kalkınma bankası programı", "Eximbank alıcı kredisi isteniyor", "Belirlenmedi"],
    },
    received: {
      title: "Alındı. Süre işliyor.",
      body: "Bir muhatap 48 saat içinde sizin dilinizde yanıt verir. Bilgi eksikse önce e-posta veya WhatsApp ile size ulaşılır.",
      steps: ["Talep uygun sektöre yönlendirildi", "Varsa eksik veriler istenir", "Fiyat, teslim süresi, Incoterms, ödeme ve QC kapsamıyla teklif"],
      ref: "Referans",
      home: "Siteye dön",
    },
  },
  languagesNote: "İngilizce, Türkçe ve Fransızca yayında. Arapça sonraki aşamada gelecek.",
  a11y: {"keyFigures": "Temel rakamlar", "routeDiagram": "Fabrikalardan tek konteyner üzerinden limanlara ve dünyaya rota", "marketsMap": "Aktif pazarların haritası", "prevSteps": "Önceki adımlar", "nextSteps": "Sonraki adımlar", "language": "Dil", "primary": "Ana menü", "chapters": "Bölümler"},
  faq: {
    "title": "Sıkça sorulan sorular",
    "intro": "Alıcıların bize en çok sorduğu sorulara kısa cevaplar. Sorunuz burada yoksa bize yazın veya teklif isteyin.",
    "items": [
      {
        "q": "Insidermark nedir, kimdir?",
        "a": "Insidermark, Türkiye'nin köklü sanayi üreticileriyle çalışan ve dünya genelinde yurt dışı alıcılara hizmet veren bir tedarik ve operasyon merkezidir. Alıcının talebini doğru üreticiyle eşleştirir, üretimi sahada izler, ürünü sevkiyat öncesi doğrular ve farklı üreticilerin siparişini tek konteynerde toplar."
      },
      {
        "q": "Doğrudan fabrikadan almak yerine neden sizinle çalışmalıyım?",
        "a": "Fabrikalara doğrudan ulaşabilirsiniz ve birçok alıcı bunu yapıyor. Biz bunun üstüne üç şey katarız: partner düzeyi şartlar, sahada üretim takibi ve doğrulama, ve farklı üreticilerin siparişini tek konteynerde birleştirerek navlun ve gümrük işini azaltmak. Tek muhatapla çalışırsınız."
      },
      {
        "q": "Hangi üreticilerle çalışıyorsunuz?",
        "a": "Ağımızda Mapar (ticari araçlar), Hıdırusta (ağır vasıta yedek parça), FMC (dövme, döküm, demiryolu, hidrolik) ve Özbaşak (değirmen ve tesis) yer alıyor. Katalogda olmayan ürünler için özel tedarik masamız Türkiye sanayisinden uygun üreticiyi bulur."
      },
      {
        "q": "Fiyatları nasıl belirliyorsunuz?",
        "a": "Fiyat; ürüne, hacme ve sevkiyat kapsamına göre değişir. Her teklifte fiyatın neleri kapsadığı (ürün, kalite kontrol, konsolidasyon, lojistik) açıkça yazılır."
      },
      {
        "q": "Teklifi ne kadar sürede alırım?",
        "a": "Hedefimiz 48 saattir. Teknik bilgi eksikse süre durur; sizden eksik bilgiyi isteriz. Özel imalatta 3 ila 5 gün, anahtar teslim projelerde 5 gün içinde teknik danışma görüşmesi planlanır."
      },
      {
        "q": "Minimum sipariş miktarı var mı?",
        "a": "Minimum miktar ürüne ve üreticiye göre değişir. Küçük ve karma siparişleri konsolidasyonla değerlendiririz; talebinizi iletin, size uygun yapıyı önerelim."
      },
      {
        "q": "Ödeme koşulları nelerdir?",
        "a": "Yeni alıcılar için peşin ödeme veya teyitli akreditif. Vadeli koşullar yalnızca ticari geçmiş oluştuktan sonra ve kredi sigortasıyla değerlendirilir. Ödeme koşulları yazılı olarak sabitlenmeden üretim başlamaz."
      },
      {
        "q": "Hangi Incoterms ile çalışıyorsunuz?",
        "a": "EXW (fabrika teslim), FOB (gemide teslim) ve CIF (mal bedeli, sigorta ve navlun). İhtiyacınıza göre teklif bu şartlardan biriyle verilir."
      },
      {
        "q": "Kalite kontrolünü nasıl yapıyorsunuz?",
        "a": "Siparişiniz üretim bandındayken sahaya gider, üretimi takip eder ve konteyner mühürlenmeden önce ürünü doğrularız. Size Insidermark mühürlü bir rapor veririz: fotoğraflar, ölçülen kalemler, ambalaj ve işaretleme kontrolü."
      },
      {
        "q": "QC raporu bir garanti midir?",
        "a": "Rapor, kontrol edilen kalemleri ve sonuçları gösterir; kapsam raporda belirtilir. Ürün garantisi üreticinin garantisidir ve kapsamı teklifte yazılıdır."
      },
      {
        "q": "Konsolidasyon nedir?",
        "a": "Farklı üreticilerden aldığınız ürünlerin tek lojistik noktada toplanıp tek konteynerle gönderilmesidir. Böylece her marka için ayrı navlun ve ayrı gümrük işlemi yapmazsınız."
      },
      {
        "q": "Hangi ülkelere satış yapıyorsunuz?",
        "a": "Dünya genelinde. Ağımız Afrika'da en güçlü (Tunus, Cezayir, Fas, Libya, Mısır, Senegal, Fildişi Sahili, Gana, Nijerya, Kenya, Etiyopya, Tanzanya); Orta Doğu, Orta Asya, Avrupa, Amerika ve talebe göre diğer pazarlara da çalışıyoruz."
      },
      {
        "q": "İthalat belgelerini (SONCAP, PVoC, uygunluk sertifikası) kim yönetir?",
        "a": "Ülkenizin güncel ithalat gereklilikleri her siparişten önce resmi kaynaklardan doğrulanır. Zorunlu denetimleri yetkili denetim kuruluşları aracılığıyla koordine ederiz. Rejimler sık değiştiği için sitedeki ülke bilgileri bilgilendirme amaçlıdır."
      },
      {
        "q": "Yedek parça ve satış sonrası desteği var mı?",
        "a": "Evet. Yedek parça ve satış sonrası taahhüdümüz vardır; kapsamı ve süresi ürün ve proje için konuşulur ve teklifte yazılı olarak yer alır. Malımızın arkasındayız."
      },
      {
        "q": "Garanti süresi nedir?",
        "a": "Garanti süresi ve kapsamı ürüne ve üreticiye göre değişir; teklifte yazılı olarak belirtilir."
      },
      {
        "q": "Katalogda olmayan bir ürünü bulabilir misiniz?",
        "a": "Evet. Özel Proje ve Tedarik Masamız, Türkiye ağır sanayisinden spesifikasyonunuza uygun üreticiyi eşleştirir, maliyeti optimize eder ve üretimi sahada kendi kalite standartlarımızla takip eder. Nitelikli bir üretici bulup doğrulayamıyorsak bunu ilk cevapta söyleriz."
      },
      {
        "q": "Çizimlerim ve teknik dosyalarım gizli kalır mı?",
        "a": "Yüklenen dosyalar yalnızca teklifinizi hazırlamak için kullanılır ve seçilen üretim partneri dışında paylaşılmaz. Hassas çizimler için talep üzerine imzalı gizlilik sözleşmesi sunarız; bunu yüklemeden önce istemenizi öneririz."
      },
      {
        "q": "Hangi dillerde çalışıyorsunuz?",
        "a": "Türkçe, İngilizce ve Fransızca. Talep üzerine Arapça. Kuzey ve Batı Afrika alıcıları için Fransızca, Körfez için Arapça destek planlıyoruz."
      },
      {
        "q": "Finansman desteği sağlıyor musunuz?",
        "a": "Uygun projelerde ihracat kredisi, akreditif ve alıcı kredisi gibi finansman yapılarını sizinle birlikte değerlendiririz. Koşullar projeye ve ülkeye bağlıdır."
      },
      {
        "q": "Sevkiyat ne kadar sürer?",
        "a": "Liman ve ülkeye göre değişir. Gösterge olarak: Tunus 4-7 gün, Cezayir 5-8 gün, Fas 8-12 gün, Senegal 18-25 gün, Nijerya 20-28 gün (Türkiye limanından varış limanına). Güncel süre teklifte verilir."
      }
    ]
  },
  legal: {
    "updated": "Son güncelleme: Ekim 2026",
    "privacy": {
      "title": "Aydınlatma ve gizlilik",
      "intro": "Bu metin, Insidermark web sitesi ve teklif formu aracılığıyla işlenen kişisel verilerinize ilişkin bilgilendirmedir.",
      "sections": [
        {
          "h": "Veri sorumlusu",
          "p": [
            "Veri sorumlusu Insidermark'tır (Konya · İstanbul, Türkiye). İletişim: rfq@insidermark.com."
          ]
        },
        {
          "h": "İşlenen veriler",
          "p": [
            "Teklif formuna girdiğiniz ad, şirket, e-posta, telefon, ülke, talep bilgileri ve yüklediğiniz teknik dosyalar. Sitenin güvenliği için sınırlı teknik kayıtlar (ör. IP adresi, tarayıcı bilgisi) işlenebilir."
          ]
        },
        {
          "h": "İşleme amaçları",
          "p": [
            "Talebinizi değerlendirmek, teklif hazırlamak, sizinle iletişim kurmak, sevkiyat ve belge süreçlerini yürütmek ve yasal yükümlülükleri yerine getirmek."
          ]
        },
        {
          "h": "Hukuki sebep",
          "p": [
            "Talebiniz doğrultusunda sözleşme öncesi işlemlerin yürütülmesi, meşru menfaatlerimiz ve gerektiğinde açık rızanız."
          ]
        },
        {
          "h": "Aktarım",
          "p": [
            "Verileriniz yalnızca teklif için gerekli olan üretim partnerlerine, lojistik ve belge hizmeti sağlayıcılarına ve yasal olarak yetkili kurumlara aktarılabilir. Barındırma ve e-posta hizmetleri yurt dışındaki sağlayıcılar aracılığıyla sunulabilir; bu durumda uygun güvenceler uygulanır."
          ]
        },
        {
          "h": "Saklama",
          "p": [
            "Verileriniz, işleme amacının gerektirdiği süre boyunca ve yasal saklama yükümlülüklerine uygun olarak saklanır. Talep halinde silinir veya anonimleştirilir."
          ]
        },
        {
          "h": "Hassas dosyalar",
          "p": [
            "Gizli çizim ve teknik dosyaları yüklemeden önce bize yazarak gizlilik sözleşmesi (NDA) talep edebilirsiniz. İhracat kontrolüne tabi olabilecek teknik veriler için önce bizimle iletişime geçin."
          ]
        },
        {
          "h": "Haklarınız",
          "p": [
            "Verilerinize erişme, düzeltme, silme, işlemeye itiraz etme ve aktarımları öğrenme haklarına sahipsiniz. Başvuru için rfq@insidermark.com adresine yazabilirsiniz."
          ]
        },
        {
          "h": "Çerezler",
          "p": [
            "Site, çalışması için gerekli olmayan çerezleri varsayılan olarak kullanmaz. Gizlilik dostu istatistikler kullanılırsa bu sayfada belirtilir."
          ]
        }
      ]
    },
    "terms": {
      "title": "Kullanım koşulları",
      "intro": "Bu siteyi kullanarak aşağıdaki koşulları kabul etmiş olursunuz.",
      "sections": [
        {
          "h": "Bilgilendirme amacı",
          "p": [
            "Sitedeki içerik bilgilendirme amaçlıdır. Teklifler yazılı olarak verilir ve ancak yazılı sözleşme veya onaylı proforma ile bağlayıcı hale gelir."
          ]
        },
        {
          "h": "Ülke ve ithalat bilgileri",
          "p": [
            "İthalat gereklilikleri, denetim programları ve süreler sık değişir. Sitedeki ülke bilgileri güncel resmi kaynakların yerine geçmez; her sipariş öncesinde teyit edilir."
          ]
        },
        {
          "h": "Partner markaları",
          "p": [
            "Mapar, Hıdırusta, FMC ve Özbaşak adları ve logoları ilgili sahiplerine aittir ve partner olarak gösterilmek üzere kullanılır."
          ]
        },
        {
          "h": "Fikri haklar",
          "p": [
            "Site içeriği, tasarımı ve Insidermark adı ile mührü Insidermark'a aittir; izinsiz kopyalanamaz."
          ]
        },
        {
          "h": "Sorumluluk",
          "p": [
            "Site mevcut haliyle sunulur. Yasaların izin verdiği ölçüde, site içeriğinin kullanımından doğan dolaylı zararlardan sorumluluk kabul edilmez."
          ]
        },
        {
          "h": "Uygulanacak hukuk",
          "p": [
            "Bu koşullar Türkiye Cumhuriyeti hukukuna tabidir."
          ]
        }
      ]
    }
  },
};

export const dictionaries: Record<Lang, Dictionary> = { en, fr, tr };
export const getDict = (lang: Lang) => dictionaries[lang];
export const href = (lang: Lang, path = "") => `/${lang}${path}`;
